import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Anchor, LogOut, Plus, Settings, Trash2, ChevronDown, Camera, X, FileText, Save, Wrench, Settings2, Radio, Sailboat, Anchor as AnchorIcon, FileDown, Sparkles, Fuel, UserCircle, Info, AlertCircle, AlertTriangle, MoreHorizontal, Pencil } from "lucide-react";
import { isPdfDataUri } from "@/lib/imageUtil";
import { CURRENCIES, FIELD_PRESETS, DEFAULT_FIELDS, formatMoney } from "@/lib/entryFieldConfig";
import InstallPrompt from "@/components/InstallPrompt";
import PaywallBanner, { useBillingStatus, ManageSubscriptionLink } from "@/components/PaywallBanner";
import TrialEndingBanner from "@/components/TrialEndingBanner";
import ProGate from "@/components/ProGate";
import { isProUser } from "@/lib/plan";
import EntryModal from "@/components/EntryModal";
import ImportPartsModal from "@/components/ImportPartsModal";
import ManualsView, { ManualIntervalsModal } from "@/components/ManualsView";
import SurveyExtractModal from "@/components/SurveyExtractModal";
import { HeroPhotoPicker } from "@/components/HeroPhotoPicker";
import HybridTabBar from "@/components/HybridTabBar";
import ChecklistsView from "@/components/ChecklistsView";
import CruiserContactsView from "@/components/CruiserContactsView";
import FuelEconomyView, { MpgChart } from "@/components/FuelEconomyView";
import SeasonSummary from "@/components/SeasonSummary";
import SafetyView from "@/components/SafetyView";
import HullInsuranceCard, { computeInsuranceStatus } from "@/components/HullInsuranceCard";
import WhatsNew from "@/components/WhatsNew";
import FeedbackModal from "@/components/FeedbackModal";
import TrialDaysPill from "@/components/TrialDaysPill";
import ReviewPromptModal from "@/components/ReviewPromptModal";
import { canShowPlans, isIOSNative, isNativeApp } from "@/lib/platform";
import { openPaywall } from "@/lib/iap";

const TAB_OVERVIEW = "Dashboard";
const TAB_CONTACTS = "Contacts";
// "Checklists" and "Season" tabs — trialed on web through v1.4.x, now
// promoted to full parity across web + native (v1.5.0). Both features are
// Pro-gated via <ProGate/>, so free-tier users see the upgrade card.
const TAB_CHECKLISTS = "Checklists";
// "Season" recap tab — Pro-only aggregated season summary (fuel, cost,
// distance, engine hours) with PDF export + share-as-image. Ships on
// both web + native starting v1.5.0.
const TAB_SEASON = "Season";
// "Safety" tab — flares (42mo expiry), fire extinguishers (12yr expiry),
// and PFDs. Available to all users (Free + Pro) since safety is table-stakes.
// Sits under the "More ▼" dropdown per user decision Feb 12, 2026.
const TAB_SAFETY = "Safety";
// Primary (pinned) tabs — always visible in the HybridTabBar.
// Everything else lives under the "More ▼" reorderable dropdown.
const PRIMARY_TABS = [TAB_OVERVIEW, "Cruising Log", TAB_CONTACTS, "Activity"];

// Maintenance status state machine — used to color-code the "Maintenance" tab
// rows and the Overview "upcoming service" list. Logic mirrors the backend
// `_stats_is_overdue` helper but adds a "upcoming" warning band so captains
// can see what's coming before it's overdue.
//
//   done       — entry already completed (status set to "done")
//   overdue    — due_at < today OR due_hours <= current engine hours
//   upcoming   — due within 30 days OR within 25 engine hours of "now"
//   scheduled  — has a due date / due hours, but further out
//   null       — no due date/hours → not part of the schedule
export function computeMaintenanceStatus(entry, engineHours) {
    if (!entry) return null;
    if (entry.status === "done") return "done";
    const due_at = entry.due_at;
    const due_hours = entry.due_hours;
    if (!due_at && (due_hours == null || due_hours === "")) return null;
    const today = new Date().toISOString().slice(0, 10);
    const currentHours = Number(engineHours) || 0;
    if (due_at && String(due_at).slice(0, 10) < today) return "overdue";
    if (due_hours != null && due_hours !== "" && currentHours > 0 && Number(due_hours) <= currentHours) return "overdue";
    if (due_at) {
        const diffDays = Math.floor((new Date(due_at).getTime() - new Date(today).getTime()) / 86400000);
        if (diffDays >= 0 && diffDays <= 30) return "upcoming";
    }
    if (due_hours != null && due_hours !== "" && currentHours > 0) {
        const diffHours = Number(due_hours) - currentHours;
        if (diffHours > 0 && diffHours <= 25) return "upcoming";
    }
    return "scheduled";
}

// Re-export legacy named exports for backward compatibility with anything that
// imports them from "@/pages/Dashboard" (e.g. older tests).
export { CURRENCIES, formatMoney };

export default function Dashboard() {
    const { vesselId } = useParams();
    const nav = useNavigate();
    const { user, setUser, logout } = useAuth();

    const [vessels, setVessels] = useState([]);
    const [vessel, setVessel] = useState(null);
    const [tab, setTab] = useState(TAB_OVERVIEW);
    const [entries, setEntries] = useState([]);
    const [stats, setStats] = useState(null);
    const [fuelEconomy, setFuelEconomy] = useState([]);
    const [trips, setTrips] = useState([]);
    const [safetyItems, setSafetyItems] = useState([]);
    const [showEntry, setShowEntry] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [archiveStatus, setArchiveStatus] = useState(null);
    const [showImportParts, setShowImportParts] = useState(false);
    const [intervalsManual, setIntervalsManual] = useState(null);
    const [hoursInput, setHoursInput] = useState("");
    const [vesselMenuOpen, setVesselMenuOpen] = useState(false);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [err, setErr] = useState("");
    const billing = useBillingStatus(vessel?.id ? 1 : 0);
    // For shared vessels (viewer/editor/admin), the OWNER pays — don't gate
    // writes on the current user's billing. Viewers are always read-only.
    const viewerRole = vessel?.viewer_role || "owner";
    const isOwner = viewerRole === "owner";
    const isViewerOnly = viewerRole === "viewer";
    // Freemium (v1.5.0): free tier includes 1 vessel. A free owner with extra
    // vessels (grandfathered from the trial era) can only write to their
    // OLDEST vessel — the others are read-only. Mirrors the backend rule in
    // _resolve_vessel_for_write (server.py).
    const isPro = isProUser(user);
    const ownedVessels = useMemo(
        () => vessels.filter((v) => v.user_id === user?.id),
        [vessels, user?.id]
    );
    const vesselLockedForFree =
        isOwner && !isPro && ownedVessels.length > 1 && vessel?.id !== ownedVessels[0]?.id;
    const canWrite = !isViewerOnly && !vesselLockedForFree;

    // Load vessels list
    useEffect(() => {
        api.get("/vessels").then(({ data }) => {
            setVessels(data);
            if (data.length === 0) {
                nav("/onboarding", { replace: true });
                return;
            }
            const target = vesselId ? data.find((v) => v.id === vesselId) : data[0];
            if (!target) {
                nav(`/app/${data[0].id}`, { replace: true });
            } else if (!vesselId) {
                nav(`/app/${target.id}`, { replace: true });
            } else {
                setVessel(target);
            }
        }).catch(() => {});
    }, [vesselId, nav]);

    // Reload entries + stats when vessel changes
    useEffect(() => {
        if (!vessel) return;
        loadEntries();
        loadStats();
        loadFuelEconomy();
        loadTrips();
        loadSafetyItems();
        setHoursInput(String(vessel.engine_hours || ""));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vessel?.id]);

    const loadEntries = async () => {
        if (!vessel) return;
        const { data } = await api.get(`/vessels/${vessel.id}/entries`);
        setEntries(data);
        // Archive gate (Oct 2026 release switch) — free tier sees a banner
        // when older seasons are archived behind Pro. Non-fatal if it fails.
        try {
            const { data: arch } = await api.get(`/vessels/${vessel.id}/entries-archive-status`);
            setArchiveStatus(arch);
        } catch {
            setArchiveStatus(null);
        }
    };
    const loadStats = async () => {
        if (!vessel) return;
        const { data } = await api.get(`/vessels/${vessel.id}/stats`);
        setStats(data);
    };
    const loadFuelEconomy = async () => {
        if (!vessel) return;
        try {
            const { data } = await api.get(`/vessels/${vessel.id}/fuel-economy`);
            setFuelEconomy(data);
        } catch (e) {
            // Non-fatal — chart just won't show
            console.warn("[dashboard] fuel-economy load failed", e?.response?.status);
        }
    };
    const loadTrips = async () => {
        if (!vessel) return;
        try {
            const { data } = await api.get(`/vessels/${vessel.id}/trips`);
            setTrips(data);
        } catch (e) {
            console.warn("[dashboard] trips load failed", e?.response?.status);
        }
    };
    const loadSafetyItems = async () => {
        if (!vessel) return;
        try {
            const { data } = await api.get(`/vessels/${vessel.id}/safety-items`);
            setSafetyItems(data || []);
        } catch (e) {
            console.warn("[dashboard] safety-items load failed", e?.response?.status);
        }
    };

    // Full ordered list of tab labels for this vessel. Insertion order used by
    // HybridTabBar to derive the "More ▼" contents (= full list minus PRIMARY_TABS).
    // Checklists + Season ship on both web and native from v1.5.0 onwards
    // (Pro-gated via <ProGate/> so free-tier users see the upgrade card).
    const categories = useMemo(
        () => {
            const base = [TAB_OVERVIEW, "Vessel Info", "Activity", TAB_CONTACTS, TAB_CHECKLISTS, TAB_SEASON, TAB_SAFETY];
            return [...base, ...(vessel?.categories || [])];
        },
        [vessel]
    );

    const visibleEntries = useMemo(() => {
        if (tab === TAB_OVERVIEW || tab === "Vessel Info" || tab === "Activity" || tab === TAB_CONTACTS || tab === TAB_CHECKLISTS || tab === TAB_SEASON || tab === TAB_SAFETY) return entries;
        return entries.filter((e) => e.category === tab);
    }, [entries, tab]);

    const updateHours = async () => {
        const n = parseFloat(hoursInput);
        if (Number.isNaN(n)) return;
        try {
            const { data } = await api.patch(`/vessels/${vessel.id}`, { engine_hours: n });
            setVessel(data);
            loadStats();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const onEntrySaved = async () => {
        setShowEntry(false);
        setEditingEntry(null);
        await loadEntries();
        await loadStats();
        await loadTrips();
        // Cruising Log entries with end_hours auto-sync the vessel's engine
        // hours on the backend (see routes/entries.py). Refetch so the
        // dashboard's ENGINE HOURS tile reflects the new value without a
        // page reload.
        try {
            const { data } = await api.get(`/vessels/${vessel.id}`);
            setVessel(data);
        } catch (e) {
            console.warn("[dashboard] vessel refetch after save failed", e?.response?.status);
        }
    };

    const deleteEntry = (idOrEntry) => {
        const entry = typeof idOrEntry === "string"
            ? entries.find((x) => x.id === idOrEntry) || { id: idOrEntry }
            : idOrEntry;
        setConfirmDelete(entry);
    };

    const performDelete = async () => {
        const entry = confirmDelete;
        if (!entry || deleting) return;
        setDeleting(true);
        try {
            await api.delete(`/vessels/${vessel.id}/entries/${entry.id}`);
            setConfirmDelete(null);
            await loadEntries();
            await loadStats();
            if (entry.category === "Cruising Log") {
                await loadTrips();
                // Engine hours may have been reconciled down on the backend —
                // refetch the vessel so the ENGINE HOURS tile updates.
                try {
                    const { data } = await api.get(`/vessels/${vessel.id}`);
                    setVessel(data);
                } catch (e2) {
                    console.warn("[dashboard] vessel refetch after delete failed", e2?.response?.status);
                }
            }
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete entry");
            setConfirmDelete(null);
        } finally {
            setDeleting(false);
        }
    };

    const [pdfBusy, setPdfBusy] = useState(false);
    const exportPdf = async () => {
        if (!vessel || pdfBusy) return;
        setPdfBusy(true);
        try {
            const res = await api.get(`/vessels/${vessel.id}/export-pdf`, { responseType: "blob" });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${vessel.name.replace(/\s+/g, "_")}_log.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => window.URL.revokeObjectURL(url), 1500);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not generate PDF");
        } finally {
            setPdfBusy(false);
        }
    };

    if (!vessel) return null;

    const heroBg = vessel.hero_photo_url || "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=1600";
    const fieldsForTab = FIELD_PRESETS[tab] || DEFAULT_FIELDS;
    // Web → Stripe pricing page; iOS AND Android native → RevenueCat paywall
    // (Guideline 3.1.1 for App Store, equivalent Google Play policy).
    // Previously this only checked isIOSNative() which broke the Android
    // trial-badge click flow (Feb 8, 2026 fix — Sean reported Android build 28
    // trial pill did nothing).
    const goPlans = () => (isNativeApp() ? openPaywall() : nav("/pricing"));

    return (
        <div data-testid="dashboard-page">
            {isOwner && <PaywallBanner status={billing} />}
            {isOwner && <TrialEndingBanner />}
            {isOwner && <ReviewPromptModal />}
            <InstallPrompt />
            {/* Hero */}
            <div className="bm-hero" style={{ minHeight: 280 }}>
                <div className="bm-hero-bg" style={{ backgroundImage: `url(${heroBg})` }} />
                <div className="bm-hero-overlay" />
                <div className="bm-hero-content" style={{ maxWidth: 1480, margin: "0 auto", padding: "22px 32px 32px" }}>
                    {/* Top bar */}
                    <div className="bm-mobile-stack" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 36, gap: 10, flexWrap: "wrap" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                            <img
                                src="/brand/bm-anchor-mark.png"
                                alt=""
                                width="26"
                                height="26"
                                data-testid="dashboard-logo-mark"
                                style={{
                                    borderRadius: 6,
                                    boxShadow: "0 3px 12px -3px rgba(212, 181, 114, 0.28), inset 0 0 0 1px rgba(212, 181, 114, 0.35)",
                                    flexShrink: 0,
                                }}
                            />
                            <span className="bm-display" style={{ fontSize: 20 }}>Bosun&apos;s Mate</span>
                            {isOwner && <TrialDaysPill />}
                            <span style={{ width: 1, height: 18, background: "var(--bm-border)" }} />
                            <VesselSwitcher
                                vessels={vessels}
                                current={vessel}
                                open={vesselMenuOpen}
                                setOpen={setVesselMenuOpen}
                                onPick={(v) => {
                                    setVesselMenuOpen(false);
                                    nav(`/app/${v.id}`);
                                }}
                                onAdd={() => {
                                    setVesselMenuOpen(false);
                                    // Free plan includes 1 vessel — route to plans instead
                                    if (!isPro && ownedVessels.length >= 1) goPlans();
                                    else nav("/onboarding");
                                }}
                            />
                            {!isOwner && (
                                <span
                                    data-testid="viewer-role-badge"
                                    className="bm-tag"
                                    style={{
                                        fontSize: 11,
                                        padding: "3px 9px",
                                        color: "var(--bm-gold)",
                                        borderColor: "var(--bm-gold-dim)",
                                        background: "rgba(212,181,114,0.08)",
                                        textTransform: "capitalize",
                                    }}
                                    title={`You have ${viewerRole} access on this shared vessel`}
                                >
                                    {viewerRole}
                                </span>
                            )}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            {canShowPlans() && isOwner && !isPro && (
                                <button
                                    data-testid="free-plan-badge"
                                    onClick={goPlans}
                                    className="bm-tag"
                                    style={{ cursor: "pointer", color: "var(--bm-gold)", background: "rgba(212, 181, 114, 0.08)", border: "1px solid var(--bm-gold-dim)" }}
                                    title="See Pro plans"
                                >
                                    Free plan · Upgrade →
                                </button>
                            )}
                            {canShowPlans() && isOwner && isPro && user?.subscription_status === "free" && user?.pro_until && (
                                <button
                                    data-testid="pro-trial-badge"
                                    onClick={goPlans}
                                    className="bm-tag bm-tag-warn"
                                    style={{ cursor: "pointer", background: "rgba(251, 191, 36, 0.10)", border: "1px solid rgba(251, 191, 36, 0.45)" }}
                                    title="Keep Pro — see plans"
                                >
                                    Pro trial · {daysLeft(user.pro_until)} days left →
                                </button>
                            )}
                            {canShowPlans() && isOwner && user?.subscription_status === "active" && (
                                <span className="bm-tag bm-tag-good" data-testid="active-badge">
                                    {user.subscription_plan === "annual" ? "Annual" : "Monthly"}
                                </span>
                            )}
                            {canShowPlans() && isOwner && user?.subscription_status === "lifetime" && (
                                <span className="bm-tag" data-testid="lifetime-badge" style={{ color: "var(--bm-gold)", borderColor: "var(--bm-gold-dim)" }}>
                                    ⚓ Founder
                                </span>
                            )}
                            {canShowPlans() && isOwner && (user?.subscription_status === "active" || user?.subscription_status === "lifetime") && (
                                <ManageSubscriptionLink />
                            )}
                            <button data-testid="settings-btn" className="bm-btn bm-btn-ghost" onClick={() => nav(`/app/${vessel.id}/settings`)} style={{ padding: "8px 12px" }}>
                                <Settings size={15} /> <span className="bm-mobile-hide-label">Settings</span>
                            </button>
                            <button
                                data-testid="export-pdf-btn"
                                className="bm-btn bm-btn-ghost"
                                onClick={isPro ? exportPdf : goPlans}
                                disabled={pdfBusy}
                                style={{ padding: "8px 12px", opacity: pdfBusy ? 0.6 : 1 }}
                                title={isPro ? "Download full vessel history as PDF" : "PDF export is a Pro feature — see plans"}
                            >
                                <FileDown size={15} /> <span className="bm-mobile-hide-label">{pdfBusy ? "Building…" : "Export PDF"}</span>
                            </button>
                            <button data-testid="account-btn" className="bm-btn bm-btn-ghost" onClick={() => nav("/account")} style={{ padding: "8px 12px" }} title="Account settings">
                                <UserCircle size={15} /> <span className="bm-mobile-hide-label">Account</span>
                            </button>
                            <button data-testid="logout-btn" className="bm-btn bm-btn-ghost" onClick={async () => { nav("/", { replace: true }); await logout(); }} style={{ padding: "8px 12px" }}>
                                <LogOut size={15} /> <span className="bm-mobile-hide-label">Sign out</span>
                            </button>
                        </div>
                    </div>

                    {/* Hero title */}
                    <div style={{ fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase", color: "var(--bm-text-muted)" }}>
                        Vessel Management System
                    </div>
                    <h1 className="bm-display bm-hero-vessel-name" style={{ fontSize: "clamp(48px, 8vw, 96px)", lineHeight: 1, margin: "6px 0 8px", fontWeight: 500, wordBreak: "break-word" }}>
                        {vessel.name}
                    </h1>
                    {(vessel.location || vessel.tagline) && (
                        <div style={{ color: "var(--bm-text-muted)", fontSize: 14 }}>
                            {vessel.location}
                            {vessel.location && vessel.tagline ? " · " : ""}
                            {vessel.tagline}
                        </div>
                    )}

                    {/* Stat row */}
                    {stats && (
                        <div data-testid="stat-row" style={{ marginTop: 28, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, maxWidth: 920 }}>
                            {(vessel.engines || []).length >= 2 ? (
                                <EnginesStatTile engines={vessel.engines} hint="Auto-updates when you save a Cruising Log entry with end hours." />
                            ) : (
                                <StatTile testid="stat-engine-hours" label="Engine Hours" value={stats.engine_hours?.toFixed?.(1) ?? "—"} hint="Auto-updates when you save a Cruising Log entry with end hours." />
                            )}
                            <StatTile testid="stat-overdue" label="Overdue Items" value={stats.overdue_items ?? 0} highlight={stats.overdue_items > 0} />
                            <StatTile testid="stat-ytd-cost" label={`YTD Costs${stats.ytd_currency && stats.ytd_currency !== "USD" ? ` (${stats.ytd_currency})` : ""}`} value={formatMoney(stats.ytd_cost || 0, stats.ytd_currency || "USD")} />
                            <StatTile testid="stat-total-fuel" label="Total Fuel" value={(stats.total_fuel || 0).toLocaleString()} />
                            {stats.ytd_distance_nm > 0 && (
                                <StatTile testid="stat-ytd-distance" label="YTD Distance" value={`${stats.ytd_distance_nm.toLocaleString()} nm`} />
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div style={{ borderBottom: "1px solid var(--bm-border-soft)", background: "var(--bm-bg-1)" }}>
                <div style={{ maxWidth: 1480, margin: "0 auto", padding: "0 32px" }}>
                    <HybridTabBar
                        vesselName={vessel.name}
                        primary={PRIMARY_TABS}
                        all={categories}
                        currentTab={tab}
                        onTabChange={setTab}
                        userTabOrder={user?.tab_order || []}
                        onOrderChange={(newOrder) => setUser((u) => u ? { ...u, tab_order: newOrder } : u)}
                    />
                </div>
            </div>

            {/* Main */}
            <main style={{ maxWidth: 1480, margin: "0 auto", padding: "32px clamp(16px, 4vw, 32px)" }}>
                {err && <div style={{ color: "var(--bm-bad)", marginBottom: 14 }}>{err}</div>}

                {vesselLockedForFree && (
                    <div data-testid="free-vessel-readonly-banner" className="bm-card" style={{ padding: 14, marginBottom: 18, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", color: "var(--bm-warn)", fontSize: 14 }}>
                        <span>Read-only — the Free plan includes 1 vessel (your first). Upgrade to Pro to keep logging on all your vessels.</span>
                        <button data-testid="free-vessel-upgrade-btn" className="bm-btn bm-btn-gold" onClick={goPlans} style={{ padding: "6px 14px", fontSize: 13 }}>
                            See Pro plans
                        </button>
                    </div>
                )}

                {archiveStatus?.gate_active && archiveStatus.archived_count > 0 && (
                    <div data-testid="archive-gate-banner" className="bm-card" style={{ padding: 14, marginBottom: 18, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", fontSize: 14 }}>
                        <span style={{ color: "var(--bm-text-muted)" }}>
                            <strong style={{ color: "var(--bm-text)" }}>{archiveStatus.archived_count} {archiveStatus.archived_count === 1 ? "entry" : "entries"} from previous seasons {archiveStatus.archived_count === 1 ? "is" : "are"} archived</strong> — stored safe, never deleted. Pro unlocks your vessel's full history.
                        </span>
                        <button data-testid="archive-gate-upgrade-btn" className="bm-btn bm-btn-gold" onClick={goPlans} style={{ padding: "6px 14px", fontSize: 13 }}>
                            Unlock full history
                        </button>
                    </div>
                )}

                <SafetyAlertBanner
                    items={safetyItems}
                    vessel={vessel}
                    onOpenSafety={() => setTab(TAB_SAFETY)}
                    onOpenInsurance={() => setTab("Insurance")}
                    active={tab === TAB_SAFETY || tab === "Insurance"}
                    isPro={isPro}
                />

                {tab === TAB_OVERVIEW && (
                    <Overview vessel={vessel} entries={entries} fuelEconomy={fuelEconomy} hoursInput={hoursInput} setHoursInput={setHoursInput} updateHours={updateHours} canWrite={canWrite} viewerRole={viewerRole} isPro={isPro} />
                )}
                {tab === "Vessel Info" && (
                    <VesselInfo vessel={vessel} onChange={setVessel} canWrite={canWrite} />
                )}
                {tab === "Activity" && (
                    <ActivityView entries={entries} engines={vessel?.engines || []} currentUserId={user?.id} />
                )}
                {tab === TAB_CONTACTS && (
                    <CruiserContactsView
                        heading="People you've met on the water"
                        subheading="Because you recognize boats better than people. Log the hull, remember the crew, find them next season."
                    />
                )}
                {tab === TAB_CHECKLISTS && (
                    <ProGate
                        feature="checklists"
                        title="Checklists"
                        description="Winterizing, spring commissioning and pre-departure checklists — with per-item notes and a full history of every run. Part of Bosun's Mate Pro."
                    >
                        <ChecklistsView vesselId={vessel.id} vesselName={vessel.name} />
                    </ProGate>
                )}
                {tab === TAB_SEASON && (
                    <ProGate
                        feature="season-summary"
                        title="Season Summary"
                        description="A rolled-up recap of every season — distance covered, engine hours, fuel used and total spend. Downloadable as PDF and shareable as an image. Part of Bosun's Mate Pro."
                    >
                        <SeasonSummary vessel={vessel} />
                    </ProGate>
                )}
                {tab === TAB_SAFETY && (
                    <SafetyView
                        vesselId={vessel.id}
                        canWrite={canWrite}
                        onItemsChanged={loadSafetyItems}
                    />
                )}
                {tab !== TAB_OVERVIEW && tab !== "Vessel Info" && tab !== "Activity" && tab !== TAB_CONTACTS && tab !== TAB_CHECKLISTS && tab !== TAB_SEASON && tab !== TAB_SAFETY && (
                    (FIELD_PRESETS[tab] || DEFAULT_FIELDS).isFuelEconomy ? (
                        <ProGate
                            feature="fuel-economy"
                            title="Fuel Economy & Charts"
                            description="RPM performance curves, engine-hours trends and fuel-cost analytics — find your boat's sweet spot and watch costs per nautical mile. Part of Bosun's Mate Pro."
                        >
                            <FuelEconomyView
                                vessel={vessel}
                                canWrite={canWrite}
                                onVesselPatched={setVessel}
                            />
                        </ProGate>
                    ) : (FIELD_PRESETS[tab] || DEFAULT_FIELDS).isCruisingLog ? (
                        <CruisingLogView
                            category={tab}
                            entries={visibleEntries}
                            trips={trips}
                            vesselId={vessel.id}
                            canWrite={canWrite}
                            currentUserId={user?.id}
                            engines={vessel.engines || []}
                            onAdd={() => { setEditingEntry(null); setShowEntry(true); }}
                            onEdit={(e) => { setEditingEntry(e); setShowEntry(true); }}
                            onDelete={deleteEntry}
                            onTripsChanged={loadTrips}
                            onAddEntryForTrip={(tripId) => {
                                setEditingEntry({ category: "Cruising Log", trip_id: tripId, date: new Date().toISOString().slice(0, 10) });
                                setShowEntry(true);
                            }}
                        />
                    ) : (FIELD_PRESETS[tab] || DEFAULT_FIELDS).isManuals ? (
                        <ManualsView
                            vessel={vessel}
                            entries={visibleEntries}
                            canWrite={canWrite}
                            currentUserId={user?.id}
                            onAdd={() => { setEditingEntry(null); setShowEntry(true); }}
                            onEdit={(e) => { setEditingEntry(e); setShowEntry(true); }}
                            onDelete={deleteEntry}
                            onExtractIntervals={(m) => setIntervalsManual(m)}
                        />
                    ) : (FIELD_PRESETS[tab] || DEFAULT_FIELDS).isReference ? (
                        <ReferenceCategoryView
                            category={tab}
                            entries={visibleEntries}
                            subtitle={FIELD_PRESETS[tab]?.subtitle}
                            canWrite={canWrite}
                            customFields={(vessel.custom_fields || {})[tab] || []}
                            currentUserId={user?.id}
                            onAdd={() => { setEditingEntry(null); setShowEntry(true); }}
                            onEdit={(e) => { setEditingEntry(e); setShowEntry(true); }}
                            onDelete={deleteEntry}
                            onImport={tab === "Parts" ? (isPro ? () => setShowImportParts(true) : goPlans) : null}
                        />
                    ) : (
                        <>
                            {tab === "Insurance" && (
                                <HullInsuranceCard
                                    vessel={vessel}
                                    isPro={isPro}
                                    canWrite={canWrite}
                                    onVesselPatched={setVessel}
                                    onGoPro={goPlans}
                                />
                            )}
                            <CategoryView
                                category={tab}
                                entries={visibleEntries}
                                fields={fieldsForTab}
                                canWrite={canWrite}
                                engineHours={vessel.engine_hours}
                                customFields={(vessel.custom_fields || {})[tab] || []}
                                currentUserId={user?.id}
                                onAdd={() => { setEditingEntry(null); setShowEntry(true); }}
                                onEdit={(e) => { setEditingEntry(e); setShowEntry(true); }}
                                onDelete={deleteEntry}
                            />
                        </>
                    )
                )}
            </main>

            {showEntry && (
                <EntryModal
                    vesselId={vessel.id}
                    defaultCategory={tab !== TAB_OVERVIEW && tab !== "Vessel Info" && tab !== TAB_SEASON ? tab : "Maintenance"}
                    categories={vessel.categories}
                    initial={editingEntry}
                    fields={FIELD_PRESETS[(editingEntry?.category) || tab] || DEFAULT_FIELDS}
                    customFieldsByCat={vessel.custom_fields || {}}
                    defaultCurrency={vessel.display_currency || "USD"}
                    engines={vessel.engines || []}
                    trips={trips}
                    onClose={() => { setShowEntry(false); setEditingEntry(null); }}
                    onSaved={onEntrySaved}
                />
            )}

            {confirmDelete && (
                <DeleteConfirmModal
                    entry={confirmDelete}
                    busy={deleting}
                    onCancel={() => setConfirmDelete(null)}
                    onConfirm={performDelete}
                />
            )}

            {showImportParts && (
                <ImportPartsModal
                    vesselId={vessel.id}
                    category="Parts"
                    defaultCurrency={vessel.display_currency || "USD"}
                    hasCostsTab={(vessel.categories || []).includes("Costs")}
                    onClose={() => setShowImportParts(false)}
                    onImported={async ({ added }) => {
                        setShowImportParts(false);
                        await loadEntries();
                        await loadStats();
                        setErr("");
                        // Use a temp non-error banner via the existing err slot is ugly —
                        // log to console for now; toast TBD.
                        console.info(`[parts-import] ${added} parts added`);
                    }}
                />
            )}

            {intervalsManual && (
                <ManualIntervalsModal manual={intervalsManual} onClose={() => setIntervalsManual(null)} />
            )}

            <WhatsNew />

            <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />

            <footer data-testid="app-footer" style={{
                marginTop: 56, paddingTop: 18, paddingBottom: 24,
                borderTop: "1px solid var(--bm-border-soft)",
                textAlign: "center",
                color: "var(--bm-text-dim)", fontSize: 12,
            }}>
                <button
                    type="button"
                    data-testid="footer-feedback-btn"
                    onClick={() => setFeedbackOpen(true)}
                    className="bm-link"
                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit", color: "var(--bm-gold)" }}
                >
                    Send feedback
                </button>
                {" · "}
                <Link to="/terms" data-testid="footer-terms-link" className="bm-link">Terms</Link>
                {" · "}
                <Link to="/privacy" data-testid="footer-privacy-link" className="bm-link">Privacy</Link>
                {" · "}
                <Link to="/support" className="bm-link">Support</Link>
                {" · "}
                <a href="mailto:captain@bosunsmate.app" className="bm-link">captain@bosunsmate.app</a>
            </footer>
        </div>
    );
}

function VesselSwitcher({ vessels, current, open, setOpen, onPick, onAdd }) {
    return (
        <div style={{ position: "relative" }}>
            <button data-testid="vessel-switcher-btn" className="bm-btn bm-btn-ghost" onClick={() => setOpen(!open)} style={{ padding: "6px 12px", fontSize: 13 }}>
                {current.name} <ChevronDown size={14} />
            </button>
            {open && (
                <div data-testid="vessel-switcher-menu" className="bm-card" style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, minWidth: 240, padding: 8, zIndex: 10 }}>
                    {vessels.map((v) => (
                        <button key={v.id} data-testid={`vessel-pick-${v.id}`} onClick={() => onPick(v)} style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", background: v.id === current.id ? "rgba(212, 181, 114, 0.08)" : "transparent", border: "none", color: "var(--bm-text)", cursor: "pointer", borderRadius: 6, fontSize: 13 }}>
                            {v.name}
                            <div style={{ fontSize: 11, color: "var(--bm-text-muted)" }}>{v.location || v.vessel_type}</div>
                        </button>
                    ))}
                    <div className="bm-divider" style={{ margin: "6px 0" }} />
                    <button data-testid="vessel-add-btn" onClick={onAdd} style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", textAlign: "left", padding: "8px 10px", background: "transparent", border: "none", color: "var(--bm-gold)", cursor: "pointer", borderRadius: 6, fontSize: 13 }}>
                        <Plus size={14} /> Add a vessel
                    </button>
                </div>
            )}
        </div>
    );
}

function StatTile({ label, value, highlight, testid, hint }) {
    return (
        <div className="bm-stat" data-testid={testid}>
            <div className="bm-stat-label" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                {label}
                {hint && (
                    <span
                        data-testid={`${testid}-hint`}
                        title={hint}
                        aria-label={hint}
                        style={{ display: "inline-flex", cursor: "help", color: "var(--bm-text-muted)", opacity: 0.7 }}
                    >
                        <Info size={12} />
                    </span>
                )}
            </div>
            <div className="bm-stat-value" style={{ color: highlight ? "var(--bm-warn)" : undefined }}>{value}</div>
        </div>
    );
}

// For multi-engine vessels — shows each engine's hours separately instead of
// blindly summing them (twin/quad engine boats track hours per engine).
function EnginesStatTile({ engines = [], hint }) {
    return (
        <div className="bm-stat" data-testid="stat-engine-hours" style={{ gridColumn: engines.length >= 3 ? "span 2" : undefined }}>
            <div className="bm-stat-label" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                Engine Hours
                {hint && (
                    <span
                        data-testid="stat-engine-hours-hint"
                        title={hint}
                        aria-label={hint}
                        style={{ display: "inline-flex", cursor: "help", color: "var(--bm-text-muted)", opacity: 0.7 }}
                    >
                        <Info size={12} />
                    </span>
                )}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px", marginTop: 4 }}>
                {engines.map((eng) => (
                    <div key={eng.id || eng.label} data-testid={`stat-engine-${(eng.label || "").toLowerCase().replace(/\s+/g, "-")}`} style={{ minWidth: 60 }}>
                        <div style={{ fontSize: 11, color: "var(--bm-text-muted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                            {eng.label || "Engine"}
                        </div>
                        <div className="bm-stat-value" style={{ fontSize: "clamp(20px, 3vw, 28px)" }}>
                            {Number(eng.hours_current || 0).toFixed(1)}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Overview({ vessel, entries, fuelEconomy = [], hoursInput, setHoursInput, updateHours, canWrite = true, viewerRole = "owner", isPro = false }) {
    const display = (vessel.display_currency || "USD").toUpperCase();
    const isMultiEngine = (vessel.engines || []).length >= 2;
    const fx = { ...(vessel.fx_rates || {}), [display]: 1 };
    const convert = (amount, ccy) => {
        const c = (ccy || display).toUpperCase();
        const rate = fx[c];
        if (!rate) return parseFloat(amount) || 0; // unknown ccy → assume same currency
        return (parseFloat(amount) || 0) * rate;
    };
    const upcoming = entries.filter((e) => e.status === "scheduled").slice(0, 5);
    const recentFuel = entries.filter((e) => e.category === "Fuel").slice(0, 5);
    const costByCat = entries.reduce((acc, e) => {
        const k = e.category;
        acc[k] = (acc[k] || 0) + convert(e.amount, e.currency);
        return acc;
    }, {});
    const topCosts = Object.entries(costByCat).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).slice(0, 5);

    return (
        <div data-testid="overview">
            {viewerRole === "viewer" && (
                <div data-testid="viewer-readonly-banner" className="bm-card" style={{ padding: 14, marginBottom: 16, color: "var(--bm-warn)", fontSize: 14 }}>
                    Read-only access — you can browse this vessel's log, but can't add or change entries.
                </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16, marginBottom: 24 }}>
                <div>
                    <h2 className="bm-serif" style={{ fontSize: 32, margin: 0, fontWeight: 500 }}>Overview</h2>
                    <p style={{ color: "var(--bm-text-muted)", margin: "4px 0 0", fontSize: 14 }}>Live vessel status</p>
                </div>
                {canWrite && !isMultiEngine && (
                    <div style={{ display: "flex", gap: 8 }}>
                        <input data-testid="engine-hours-input" className="bm-input" placeholder="Engine hours" value={hoursInput} onChange={(e) => setHoursInput(e.target.value)} style={{ maxWidth: 160 }} />
                        <button data-testid="update-hours-btn" className="bm-btn bm-btn-primary" onClick={updateHours}>Update hours</button>
                    </div>
                )}
                {canWrite && isMultiEngine && (
                    <div data-testid="multi-engine-hours-hint" style={{ fontSize: 12, color: "var(--bm-text-muted)", maxWidth: 240, textAlign: "right" }}>
                        Update per-engine hours in <strong>Vessel Info</strong> or <strong>⚙️ Settings → Engines</strong>.
                    </div>
                )}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                <Panel title="🔧 Upcoming Service">
                    {upcoming.length === 0 && <Empty>No service scheduled. Add an entry under Maintenance with a due date or due-hours.</Empty>}
                    {upcoming.map((e) => (
                        <Row key={e.id} title={e.title} sub={`${e.category} · due ${e.due_at || `${e.due_hours} hrs`}`} />
                    ))}
                </Panel>
                <Panel title={`💰 Cost Breakdown · ${display}`}>
                    {topCosts.length === 0 && <Empty>Log a few expenses and we'll start grouping by category here.</Empty>}
                    {topCosts.map(([k, v]) => (
                        <Row key={k} title={k} sub={formatMoney(v, display)} />
                    ))}
                </Panel>
            </div>
            <Panel title="⛽ Recent Fuel">
                {recentFuel.length === 0 && <Empty>No fuel logged yet.</Empty>}
                {recentFuel.map((e) => (
                    <Row key={e.id} title={`${e.quantity || "—"} ${e.unit || ""} · ${formatMoney(e.amount, e.currency)}`} sub={`${e.date || ""} ${e.vendor ? "· " + e.vendor : ""}`} />
                ))}
            </Panel>
            {isPro && fuelEconomy && fuelEconomy.length >= 2 && (
                <div style={{ marginTop: 16 }}>
                    <MpgChart rows={fuelEconomy} fuelUnit={vessel.fuel_unit || "gph"} speedUnit={vessel.speed_unit || "mph"} engines={vessel.engines || []} showPerEngine={(vessel.engines || []).length >= 2} />
                </div>
            )}
        </div>
    );
}

function VesselInfo({ vessel, onChange, canWrite = true }) {
    const empty = {
        transmission: { make: "", model: "", serial: "", gear_ratio: "", notes: "" },
        electronics: [],
        tender: { name: "", make: "", model: "", year: "", notes: "" },
        tender_motor: { make: "", model: "", serial: "", hp: "", notes: "" },
    };
    const [form, setForm] = useState({
        name: vessel.name,
        location: vessel.location || "",
        tagline: vessel.tagline || "",
        hero_photo_url: vessel.hero_photo_url || "",
        specs: { ...empty, ...(vessel.specs || {}) },
        engines: (vessel.engines || []).map((e) => ({ ...e })),
    });
    const [saved, setSaved] = useState(false);
    const [busy, setBusy] = useState(false);
    const [showSurveyExtract, setShowSurveyExtract] = useState(false);

    // Re-hydrate when the vessel prop changes (e.g. user added an engine in
    // Settings, came back here, and we need to reflect the latest engines).
    useEffect(() => {
        setForm((prev) => ({
            ...prev,
            engines: (vessel.engines || []).map((e) => ({ ...e })),
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vessel.id, JSON.stringify(vessel.engines || [])]);

    const updateEngine = (idx, key, value) => {
        setForm((s) => {
            const next = [...s.engines];
            next[idx] = { ...next[idx], [key]: value };
            return { ...s, engines: next };
        });
    };
    const updateSpec = (section, key, value) => {
        setForm((s) => ({ ...s, specs: { ...s.specs, [section]: { ...s.specs[section], [key]: value } } }));
    };
    const updateElectronic = (idx, key, value) => {
        setForm((s) => {
            const next = [...(s.specs.electronics || [])];
            next[idx] = { ...next[idx], [key]: value };
            return { ...s, specs: { ...s.specs, electronics: next } };
        });
    };
    const addElectronic = () => {
        const cid = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `el_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        setForm((s) => ({ ...s, specs: { ...s.specs, electronics: [...(s.specs.electronics || []), { _cid: cid, name: "", make: "", model: "", serial: "", mmsi: "", notes: "" }] } }));
    };
    const removeElectronic = (idx) => {
        setForm((s) => ({ ...s, specs: { ...s.specs, electronics: s.specs.electronics.filter((_, i) => i !== idx) } }));
    };

    const save = async () => {
        setBusy(true);
        try {
            const { data } = await api.patch(`/vessels/${vessel.id}`, {
                name: form.name,
                location: form.location,
                tagline: form.tagline,
                hero_photo_url: form.hero_photo_url,
                specs: form.specs,
                engines: form.engines.map((e) => ({
                    id: e.id || undefined,
                    label: (e.label || "").trim() || "Engine",
                    make: (e.make || "").trim() || null,
                    model: (e.model || "").trim() || null,
                    serial: (e.serial || "").trim() || null,
                    year: (e.year || "").toString().trim() || null,
                    hp_kw: (e.hp_kw || "").toString().trim() || null,
                    hours_current: parseFloat(e.hours_current) || 0,
                    fuel_type: e.fuel_type || null,
                    notes: (e.notes || "").trim() || null,
                })),
            });
            onChange(data);
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } finally {
            setBusy(false);
        }
    };

    const Row = VesselInfoRow;
    const Field = VesselInfoField;

    return (
        <div data-testid="vessel-info">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 14, marginBottom: 24 }}>
                <div>
                    <h2 className="bm-serif" style={{ fontSize: 32, margin: 0, fontWeight: 500 }}>Vessel Info</h2>
                    <p style={{ color: "var(--bm-text-muted)", margin: "4px 0 0", fontSize: 14 }}>
                        Engine, transmission, electronics, tender & tender motor registry
                    </p>
                </div>
                <button data-testid="info-save-btn" className="bm-btn bm-btn-gold" onClick={save} disabled={busy || !canWrite}>
                    <Save size={15} /> {busy ? "Saving…" : saved ? "Saved ✓" : "Save all changes"}
                </button>
            </div>

            {canWrite && (
                <div className="bm-card" data-testid="survey-extract-card" style={{ padding: 16, marginBottom: 16, background: "linear-gradient(135deg, rgba(212,181,114,0.10) 0%, rgba(91,185,232,0.05) 100%)", border: "1px solid rgba(212,181,114,0.35)" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 240 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                                <Sparkles size={15} color="var(--bm-gold)" />
                                <strong style={{ color: "var(--bm-gold)" }}>Save 20 minutes</strong>
                            </div>
                            <div style={{ fontSize: 13, color: "var(--bm-text-muted)", lineHeight: 1.5 }}>
                                Upload your marine survey PDF and AI will auto-fill vessel specs, engines, tanks, and queue every surveyor recommendation as a Maintenance entry. You review each field before anything is written.
                            </div>
                        </div>
                        <button
                            type="button"
                            data-testid="survey-extract-btn"
                            className="bm-btn bm-btn-primary"
                            onClick={() => setShowSurveyExtract(true)}
                        >
                            <Sparkles size={14} /> Extract from survey PDF
                        </button>
                    </div>
                </div>
            )}

            {showSurveyExtract && (
                <SurveyExtractModal
                    vesselId={vessel.id}
                    vessel={vessel}
                    onClose={() => setShowSurveyExtract(false)}
                    onApplied={async ({ recsAdded }) => {
                        setShowSurveyExtract(false);
                        // Re-fetch the vessel so the form picks up patched fields
                        try {
                            const { data } = await api.get(`/vessels/${vessel.id}`);
                            onChange(data);
                        } catch (e) {
                            console.error("[survey-apply] re-fetch failed:", e);
                        }
                        // Friendly post-apply UX
                        setSaved(true);
                        setTimeout(() => setSaved(false), 2000);
                        console.info(`[survey] applied, ${recsAdded} maintenance entries queued`);
                    }}
                />
            )}

            <div className="bm-card" style={{ padding: 26, marginBottom: 16 }}>
                <SectionHead icon={<Sailboat size={18} color="var(--bm-gold)" />} title="General" />
                <Row cols={2}>
                    <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="info-name-input" />
                    <Field label="Location / home port" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="info-location-input" />
                </Row>
                <Row cols={2}>
                    <Field label="Tagline" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} testid="info-tagline-input" placeholder="Maintenance · Costs · Fuel" />
                </Row>
                <div style={{ marginTop: 4 }}>
                    <label className="bm-label">Hero photo — shows behind the vessel name</label>
                    <HeroPhotoPicker value={form.hero_photo_url} onChange={(v) => setForm({ ...form, hero_photo_url: v })} disabled={!canWrite} />
                </div>
            </div>

            {form.engines.length === 0 ? (
                <div className="bm-card" style={{ padding: 26, marginBottom: 16 }}>
                    <SectionHead icon={<Wrench size={18} color="var(--bm-gold)" />} title="Engines" />
                    <div style={{ color: "var(--bm-text-muted)", fontSize: 13, padding: "8px 0", lineHeight: 1.6 }}>
                        No engines yet. Add them in <strong>⚙️ Settings → Engines</strong>, then come back here to fill in year, HP, and notes per engine.
                    </div>
                </div>
            ) : (
                form.engines.map((eng, i) => (
                    <div
                        key={eng.id || `eng-${i}`}
                        data-testid={`vessel-info-engine-${i}`}
                        className="bm-card"
                        style={{ padding: 26, marginBottom: 16 }}
                    >
                        <SectionHead
                            icon={<Wrench size={18} color="var(--bm-gold)" />}
                            title={form.engines.length === 1 ? "Engine" : `Engine — ${eng.label || `#${i + 1}`}`}
                        />
                        <Row cols={2}>
                            <Field
                                label="Label"
                                value={eng.label || ""}
                                onChange={(v) => updateEngine(i, "label", v)}
                                testid={`vessel-info-engine-label-${i}`}
                                placeholder={i === 0 ? "Port" : i === 1 ? "Starboard" : `Engine ${i + 1}`}
                            />
                            <Field
                                label="Current hours"
                                value={eng.hours_current ?? 0}
                                onChange={(v) => updateEngine(i, "hours_current", v)}
                                testid={`vessel-info-engine-hours-${i}`}
                                type="number"
                            />
                        </Row>
                        <Row cols={2}>
                            <Field
                                label="Make"
                                value={eng.make || ""}
                                onChange={(v) => updateEngine(i, "make", v)}
                                testid={`vessel-info-engine-make-${i}`}
                                placeholder="e.g. Volvo Penta"
                            />
                            <Field
                                label="Model"
                                value={eng.model || ""}
                                onChange={(v) => updateEngine(i, "model", v)}
                                testid={`vessel-info-engine-model-${i}`}
                                placeholder="e.g. D4-260"
                            />
                        </Row>
                        <Row cols={3}>
                            <Field
                                label="Serial #"
                                value={eng.serial || ""}
                                onChange={(v) => updateEngine(i, "serial", v)}
                                testid={`vessel-info-engine-serial-${i}`}
                            />
                            <Field
                                label="Year"
                                value={eng.year || ""}
                                onChange={(v) => updateEngine(i, "year", v)}
                                testid={`vessel-info-engine-year-${i}`}
                            />
                            <Field
                                label="HP / kW"
                                value={eng.hp_kw || ""}
                                onChange={(v) => updateEngine(i, "hp_kw", v)}
                                testid={`vessel-info-engine-hp-${i}`}
                                placeholder="e.g. 260 hp / 194 kW"
                            />
                        </Row>
                        <Row cols={1}>
                            <Field
                                label="Fuel type"
                                value={eng.fuel_type || ""}
                                onChange={(v) => updateEngine(i, "fuel_type", v)}
                                testid={`vessel-info-engine-fuel-${i}`}
                                placeholder="Diesel / Gasoline"
                            />
                        </Row>
                        <Field
                            label="Notes"
                            value={eng.notes || ""}
                            onChange={(v) => updateEngine(i, "notes", v)}
                            testid={`vessel-info-engine-notes-${i}`}
                            area
                        />
                    </div>
                ))
            )}

            {/* Fuel tanks — read-only summary; configure capacity in Settings → Fuel Tanks */}
            <FuelTanksSummary tanks={vessel.tanks || []} />

            <div className="bm-card" style={{ padding: 26, marginBottom: 16 }}>
                <SectionHead icon={<Settings2 size={18} color="var(--bm-gold)" />} title="Transmission / Gearbox" />
                <Row cols={2}>
                    <Field label="Make" value={form.specs.transmission.make} onChange={(v) => updateSpec("transmission", "make", v)} testid="trans-make-input" />
                    <Field label="Model" value={form.specs.transmission.model} onChange={(v) => updateSpec("transmission", "model", v)} testid="trans-model-input" />
                </Row>
                <Row cols={2}>
                    <Field label="Serial #" value={form.specs.transmission.serial} onChange={(v) => updateSpec("transmission", "serial", v)} testid="trans-serial-input" />
                    <Field label="Gear ratio" value={form.specs.transmission.gear_ratio} onChange={(v) => updateSpec("transmission", "gear_ratio", v)} testid="trans-ratio-input" placeholder="e.g. 2.04:1" />
                </Row>
                <Field label="Notes" value={form.specs.transmission.notes} onChange={(v) => updateSpec("transmission", "notes", v)} testid="trans-notes-input" area />
            </div>

            <div className="bm-card" style={{ padding: 26, marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <SectionHead icon={<Radio size={18} color="var(--bm-gold)" />} title="Electronics" />
                    <button data-testid="add-electronic-btn" type="button" className="bm-btn bm-btn-ghost" onClick={addElectronic} disabled={!canWrite}>
                        <Plus size={14} /> Add device
                    </button>
                </div>
                {(form.specs.electronics || []).length === 0 ? (
                    <div style={{ color: "var(--bm-text-muted)", fontSize: 13, padding: "12px 0" }}>
                        No electronics yet — VHF, radar, chartplotter, AIS, autopilot, etc. Tap "Add device" to start.
                    </div>
                ) : (
                    form.specs.electronics.map((el, i) => (
                        <div key={el._cid || `el-${i}`} data-testid={`electronic-row-${i}`} style={{ padding: 14, marginBottom: 10, background: "rgba(8,22,40,0.5)", borderRadius: "var(--bm-r)", border: "1px solid var(--bm-border-soft)" }}>
                            <Row cols={2}>
                                <Field label="Device" value={el.name} onChange={(v) => updateElectronic(i, "name", v)} testid={`electronic-name-${i}`} placeholder="e.g. Chartplotter" />
                                <Field label="Make" value={el.make} onChange={(v) => updateElectronic(i, "make", v)} testid={`electronic-make-${i}`} placeholder="e.g. Garmin" />
                            </Row>
                            <Row cols={3}>
                                <Field label="Model" value={el.model} onChange={(v) => updateElectronic(i, "model", v)} testid={`electronic-model-${i}`} />
                                <Field label="Serial #" value={el.serial} onChange={(v) => updateElectronic(i, "serial", v)} testid={`electronic-serial-${i}`} />
                                <Field label="MMSI" value={el.mmsi || ""} onChange={(v) => updateElectronic(i, "mmsi", v.replace(/[^0-9]/g, ""))} testid={`electronic-mmsi-${i}`} placeholder="9-digit (VHF/AIS/EPIRB)" />
                            </Row>
                            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                                <div style={{ flex: 1 }}>
                                    <Field label="Notes" value={el.notes} onChange={(v) => updateElectronic(i, "notes", v)} testid={`electronic-notes-${i}`} area />
                                </div>
                                <button type="button" data-testid={`electronic-remove-${i}`} className="bm-btn bm-btn-danger" onClick={() => removeElectronic(i)} style={{ padding: "8px 10px", marginTop: 22 }}>
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <div className="bm-card" style={{ padding: 26, marginBottom: 16 }}>
                <SectionHead icon={<AnchorIcon size={18} color="var(--bm-gold)" />} title="Tender" />
                <Row cols={2}>
                    <Field label="Name" value={form.specs.tender.name} onChange={(v) => updateSpec("tender", "name", v)} testid="tender-name-input" placeholder="e.g. Little Belle" />
                    <Field label="Make" value={form.specs.tender.make} onChange={(v) => updateSpec("tender", "make", v)} testid="tender-make-input" placeholder="e.g. Walker Bay" />
                </Row>
                <Row cols={2}>
                    <Field label="Model" value={form.specs.tender.model} onChange={(v) => updateSpec("tender", "model", v)} testid="tender-model-input" />
                    <Field label="Year" value={form.specs.tender.year} onChange={(v) => updateSpec("tender", "year", v)} testid="tender-year-input" type="number" />
                </Row>
                <Field label="Notes" value={form.specs.tender.notes} onChange={(v) => updateSpec("tender", "notes", v)} testid="tender-notes-input" area />
            </div>

            <div className="bm-card" style={{ padding: 26, marginBottom: 16 }}>
                <SectionHead icon={<Wrench size={18} color="var(--bm-gold)" />} title="Tender Motor" />
                <Row cols={2}>
                    <Field label="Make" value={form.specs.tender_motor.make} onChange={(v) => updateSpec("tender_motor", "make", v)} testid="tender-motor-make-input" placeholder="e.g. Yamaha" />
                    <Field label="Model" value={form.specs.tender_motor.model} onChange={(v) => updateSpec("tender_motor", "model", v)} testid="tender-motor-model-input" />
                </Row>
                <Row cols={2}>
                    <Field label="Serial #" value={form.specs.tender_motor.serial} onChange={(v) => updateSpec("tender_motor", "serial", v)} testid="tender-motor-serial-input" />
                    <Field label="HP" value={form.specs.tender_motor.hp} onChange={(v) => updateSpec("tender_motor", "hp", v)} testid="tender-motor-hp-input" type="number" />
                </Row>
                <Field label="Notes" value={form.specs.tender_motor.notes} onChange={(v) => updateSpec("tender_motor", "notes", v)} testid="tender-motor-notes-input" area />
            </div>

            {/* Sticky save bar at bottom for long forms */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                <button data-testid="info-save-bottom-btn" className="bm-btn bm-btn-gold" onClick={save} disabled={busy || !canWrite} style={{ padding: "12px 22px" }}>
                    <Save size={15} /> {busy ? "Saving…" : saved ? "Saved ✓" : "Save all changes"}
                </button>
            </div>
        </div>
    );
}

function SectionHead({ icon, title }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            {icon}
            <h3 className="bm-serif" style={{ fontSize: 22, margin: 0, fontWeight: 500 }}>{title}</h3>
        </div>
    );
}

// Read-only summary of the vessel's configured fuel tanks, displayed on the
// Vessel Info tab. Edits happen in Settings → Fuel Tanks.
function FuelTanksSummary({ tanks = [] }) {
    const fmt = (t) => {
        if (t.capacity == null) return "—";
        const u = t.capacity_unit || "gal_us";
        const label = u === "gal_us" ? "US gal" : u === "gal_imp" ? "Imp gal" : "L";
        return `${t.capacity} ${label}`;
    };
    // Total only makes sense when every tank uses the same unit. Otherwise show "—".
    const units = Array.from(new Set(tanks.map((t) => t.capacity_unit || "gal_us")));
    const total = (tanks.length > 0 && units.length === 1 && tanks.every((t) => t.capacity != null))
        ? tanks.reduce((s, t) => s + Number(t.capacity || 0), 0)
        : null;
    const totalUnit = units.length === 1
        ? (units[0] === "gal_us" ? "US gal" : units[0] === "gal_imp" ? "Imp gal" : "L")
        : "";
    return (
        <div data-testid="vessel-info-tanks" className="bm-card" style={{ padding: 26, marginBottom: 16 }}>
            <SectionHead icon={<Fuel size={18} color="var(--bm-gold)" />} title="Fuel Tanks" />
            {tanks.length === 0 ? (
                <div style={{ color: "var(--bm-text-muted)", fontSize: 13, padding: "8px 0", lineHeight: 1.6 }}>
                    No tanks configured. Add them in <strong>⚙️ Settings → Fuel Tanks</strong>.
                </div>
            ) : (
                <>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: total != null ? 14 : 0 }}>
                        {tanks.map((t, i) => (
                            <div
                                key={t.id || `tnk-${i}`}
                                data-testid={`vessel-info-tank-${i}`}
                                style={{ padding: 14, border: "1px solid var(--bm-border-soft)", borderRadius: "var(--bm-r)", background: "rgba(8,22,40,0.4)" }}
                            >
                                <div style={{ fontSize: 11, color: "var(--bm-text-dim)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>
                                    {t.label || "Tank"}
                                </div>
                                <div style={{ fontSize: 20, fontWeight: 500, color: "var(--bm-text)", marginBottom: 2 }}>
                                    {fmt(t)}
                                </div>
                                <div style={{ fontSize: 12, color: "var(--bm-text-muted)" }}>
                                    {(t.fuel_type || "").replace(/^./, (c) => c.toUpperCase())}
                                </div>
                            </div>
                        ))}
                    </div>
                    {total != null && (
                        <div data-testid="vessel-info-tank-total" style={{ paddingTop: 12, borderTop: "1px solid var(--bm-border-soft)", display: "flex", justifyContent: "space-between", color: "var(--bm-text-muted)", fontSize: 13 }}>
                            <span>Total capacity</span>
                            <strong style={{ color: "var(--bm-gold)", fontVariantNumeric: "tabular-nums" }}>
                                {total} {totalUnit}
                            </strong>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function ReferenceCategoryView({ category, entries, subtitle, canWrite = true, currentUserId, onAdd, onEdit, onDelete, onImport = null }) {
    const [q, setQ] = useState("");
    // Group entries that share the same OEM# / aftermarket# / title — common
    // when the same part is purchased from multiple vendors. Persist user
    // preference so it sticks across reloads. Default ON for the Parts tab
    // because that's where duplicates pile up the fastest.
    const [grouped, setGrouped] = useState(() => {
        try { return localStorage.getItem("bm-parts-grouped") !== "0"; } catch { return true; }
    });
    const [expandedKeys, setExpandedKeys] = useState(() => new Set());

    useEffect(() => {
        try { localStorage.setItem("bm-parts-grouped", grouped ? "1" : "0"); } catch (e) {
            console.warn("[parts-grouped] localStorage unavailable:", e);
        }
    }, [grouped]);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return entries;
        return entries.filter((e) =>
            [e.title, e.oem_number, e.aftermarket_number, e.vendor, e.notes]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(s))
        );
    }, [entries, q]);

    // Build the grouped row model. Each group has: key, displayTitle, oem,
    // aftermarket, vendors[], qtyTotal, unitMin/Max, currency, bestPhoto,
    // and the underlying entries[].
    const groups = useMemo(() => {
        const norm = (v) => String(v || "").trim().toLowerCase();
        const keyOf = (e) => norm(e.oem_number) || norm(e.aftermarket_number) || norm(e.title);
        const map = new Map();
        for (const e of filtered) {
            const k = keyOf(e) || `__id_${e.id}`;
            if (!map.has(k)) map.set(k, []);
            map.get(k).push(e);
        }
        const out = [];
        for (const [key, items] of map.entries()) {
            // displayTitle = longest title in the group (user-edited tends to be more descriptive)
            const displayTitle = items.reduce((best, x) =>
                ((x.title || "").length > (best || "").length ? x.title : best), items[0]?.title || "");
            const oems = Array.from(new Set(items.map((x) => x.oem_number).filter(Boolean)));
            const afters = Array.from(new Set(items.map((x) => x.aftermarket_number).filter(Boolean)));
            const vendors = Array.from(new Set(items.map((x) => x.vendor).filter(Boolean)));
            const qtyTotal = items.reduce((s, x) => s + (Number(x.quantity) || 0), 0);
            const units = items.map((x) => Number(x.amount)).filter((n) => Number.isFinite(n) && n > 0);
            const unitMin = units.length ? Math.min(...units) : null;
            const unitMax = units.length ? Math.max(...units) : null;
            const currency = items.find((x) => x.currency)?.currency || null;
            const bestPhoto = items.find((x) => x.photo_data);
            out.push({
                key,
                displayTitle,
                oems,
                afters,
                vendors,
                qtyTotal,
                unitMin,
                unitMax,
                currency,
                bestPhoto,
                items,
            });
        }
        // Sort: groups with multiple purchases first (more value to consolidate),
        // then alphabetically by display title.
        out.sort((a, b) => {
            if (a.items.length !== b.items.length) return b.items.length - a.items.length;
            return (a.displayTitle || "").localeCompare(b.displayTitle || "");
        });
        return out;
    }, [filtered]);

    // Trivial count — useMemo overhead would exceed the work itself. Inline.
    const duplicateCount = groups.filter((g) => g.items.length > 1).length;

    const toggleExpand = (key) => {
        setExpandedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    return (
        <div data-testid={`ref-view-${slug(category)}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 14, marginBottom: 20 }}>
                <div>
                    <h2 className="bm-serif" style={{ fontSize: 32, margin: 0, fontWeight: 500 }}>{category} Reference</h2>
                    <p style={{ color: "var(--bm-text-muted)", margin: "4px 0 0", fontSize: 14 }}>
                        {subtitle || "Parts catalog"} · {entries.length} parts
                        {grouped && duplicateCount > 0 && (
                            <> · <span style={{ color: "var(--bm-gold)" }}>{duplicateCount} grouped</span></>
                        )}
                    </p>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <button
                        type="button"
                        data-testid="ref-group-toggle"
                        onClick={() => setGrouped((g) => !g)}
                        title="Combine parts that share an OEM #, aftermarket #, or name"
                        style={{
                            padding: "6px 12px",
                            fontSize: 12,
                            border: `1px solid ${grouped ? "var(--bm-gold)" : "var(--bm-border-soft)"}`,
                            borderRadius: 999,
                            background: grouped ? "rgba(212,181,114,0.18)" : "rgba(8,22,40,0.5)",
                            color: grouped ? "var(--bm-gold)" : "var(--bm-text-muted)",
                            cursor: "pointer",
                            fontWeight: grouped ? 600 : 400,
                        }}
                    >
                        Group duplicates {grouped ? "· On" : "· Off"}
                    </button>
                    <input
                        data-testid="ref-search-input"
                        className="bm-input"
                        placeholder="Search..."
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        style={{ maxWidth: 220 }}
                    />
                    {onImport && canWrite && (
                        <button
                            data-testid="import-parts-btn"
                            className="bm-btn bm-btn-ghost"
                            onClick={onImport}
                            title="Use AI to read a parts receipt (PDF or photo) and import every line item at once"
                        >
                            <Sparkles size={15} color="var(--bm-gold)" /> Import from receipt
                        </button>
                    )}
                    <button data-testid="add-entry-btn" className="bm-btn bm-btn-primary" onClick={onAdd} disabled={!canWrite} style={{ opacity: canWrite ? 1 : 0.4, cursor: canWrite ? "pointer" : "not-allowed" }}>
                        <Plus size={15} /> Add part
                    </button>
                </div>
            </div>

            <div className="bm-card" style={{ overflow: "hidden" }}>
                <div style={{ overflowX: "auto" }}>
                <table className="bm-table">
                    <thead>
                        <tr>
                            <th style={{ width: 36 }}></th>
                            <th></th>
                            <th>Component</th>
                            <th>OEM #</th>
                            <th>Aftermarket #</th>
                            <th>Supplier</th>
                            <th>Unit Cost</th>
                            <th>Qty</th>
                            <th>Notes</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && (
                            <tr><td colSpan={10} style={{ textAlign: "center", padding: 36, color: "var(--bm-text-muted)" }}>
                                {entries.length === 0 ? `No parts yet — add your first ${category.toLowerCase()} part.` : "No matches for that search."}
                            </td></tr>
                        )}

                        {/* Grouped mode */}
                        {grouped && filtered.length > 0 && groups.map((g) => {
                            const isOpen = expandedKeys.has(g.key);
                            const isMulti = g.items.length > 1;
                            const unitDisplay = g.unitMin == null
                                ? "—"
                                : g.unitMin === g.unitMax
                                    ? formatMoney(g.unitMin, g.currency)
                                    : `${formatMoney(g.unitMin, g.currency)}–${formatMoney(g.unitMax, g.currency)}`;
                            const vendorDisplay = g.vendors.length === 0
                                ? "—"
                                : g.vendors.length <= 2
                                    ? g.vendors.join(" · ")
                                    : `${g.vendors.length} vendors`;
                            // Single-item groups behave like a flat row — clicking edits directly.
                            const onGroupClick = canWrite
                                ? (isMulti ? () => toggleExpand(g.key) : () => onEdit(g.items[0]))
                                : undefined;
                            return (
                                <React.Fragment key={g.key}>
                                    <tr
                                        data-testid={`group-row-${g.key}`}
                                        onClick={onGroupClick}
                                        style={{
                                            cursor: canWrite ? "pointer" : "default",
                                            background: isMulti ? "rgba(212,181,114,0.04)" : undefined,
                                        }}
                                    >
                                        <td style={{ width: 36, textAlign: "center", color: "var(--bm-text-muted)" }}>
                                            {isMulti && (
                                                <ChevronDown
                                                    size={14}
                                                    data-testid={`group-chevron-${g.key}`}
                                                    style={{ transition: "transform 0.15s ease", transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)" }}
                                                />
                                            )}
                                        </td>
                                        <td style={{ width: 56 }}>
                                            {g.bestPhoto?.photo_data ? (
                                                isPdfDataUri(g.bestPhoto.photo_data) ? (
                                                    <div style={{ width: 40, height: 40, borderRadius: 6, background: "rgba(91,185,232,0.1)", border: "1px solid var(--bm-border-soft)", display: "grid", placeItems: "center", color: "var(--bm-blue)" }}>
                                                        <FileText size={18} />
                                                    </div>
                                                ) : (
                                                    <img src={g.bestPhoto.photo_data} alt="" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6, border: "1px solid var(--bm-border-soft)" }} />
                                                )
                                            ) : (
                                                <div style={{ width: 40, height: 40, borderRadius: 6, background: "rgba(91,185,232,0.05)", border: "1px dashed var(--bm-border-soft)", display: "grid", placeItems: "center", color: "var(--bm-text-dim)" }}>
                                                    <Camera size={14} />
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ fontWeight: 600 }}>
                                            {g.displayTitle}
                                            {isMulti && (
                                                <div data-testid={`group-count-${g.key}`} style={{ fontSize: 11, color: "var(--bm-gold)", fontWeight: 500, marginTop: 3, letterSpacing: "0.04em" }}>
                                                    {g.items.length} purchases
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ color: "var(--bm-blue)", fontVariantNumeric: "tabular-nums" }}>{g.oems[0] || "—"}</td>
                                        <td style={{ color: "var(--bm-good)", fontVariantNumeric: "tabular-nums" }}>{g.afters[0] || "—"}</td>
                                        <td style={{ color: "var(--bm-text-muted)" }}>{vendorDisplay}</td>
                                        <td style={{ fontVariantNumeric: "tabular-nums" }}>{unitDisplay}</td>
                                        <td style={{ fontVariantNumeric: "tabular-nums", fontWeight: isMulti ? 600 : 400 }}>{g.qtyTotal > 0 ? g.qtyTotal : "—"}</td>
                                        <td style={{ color: "var(--bm-text-muted)", fontSize: 13 }}>
                                            {isMulti ? "" : (g.items[0].notes || "")}
                                        </td>
                                        <td style={{ textAlign: "right" }}>
                                            {canWrite && !isMulti && (
                                                <RowActions entry={g.items[0]} canWrite={canWrite} onEdit={onEdit} onDelete={onDelete} />
                                            )}
                                        </td>
                                    </tr>
                                    {/* Expanded child rows (individual purchase entries) */}
                                    {isMulti && isOpen && g.items.map((e) => (
                                        <tr key={`${g.key}-${e.id}`} data-testid={`entry-row-${e.id}`} onClick={canWrite ? () => onEdit(e) : undefined} style={{ cursor: canWrite ? "pointer" : "default", background: "rgba(8,22,40,0.4)" }}>
                                            <td></td>
                                            <td style={{ width: 56, paddingLeft: 22 }}>
                                                {e.photo_data ? (
                                                    isPdfDataUri(e.photo_data) ? (
                                                        <div style={{ width: 32, height: 32, borderRadius: 6, background: "rgba(91,185,232,0.1)", border: "1px solid var(--bm-border-soft)", display: "grid", placeItems: "center", color: "var(--bm-blue)" }}>
                                                            <FileText size={14} />
                                                        </div>
                                                    ) : (
                                                        <img src={e.photo_data} alt="" style={{ width: 32, height: 32, objectFit: "cover", borderRadius: 6, border: "1px solid var(--bm-border-soft)" }} />
                                                    )
                                                ) : (
                                                    <div style={{ width: 32, height: 32, borderRadius: 6, background: "rgba(91,185,232,0.04)", border: "1px dashed var(--bm-border-soft)", display: "grid", placeItems: "center", color: "var(--bm-text-dim)" }}>
                                                        <Camera size={12} />
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ paddingLeft: 14, color: "var(--bm-text-muted)", fontSize: 13 }}>
                                                <span style={{ marginRight: 6, color: "var(--bm-text-dim)" }}>↳</span>
                                                {e.title}
                                                {e.added_by_user_id && currentUserId && e.added_by_user_id !== currentUserId && e.added_by_name && (
                                                    <div data-testid={`entry-attrib-${e.id}`} style={{ fontSize: 11, color: "var(--bm-text-dim)", marginTop: 2 }}>
                                                        added by {e.added_by_name}
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ color: "var(--bm-blue)", fontVariantNumeric: "tabular-nums", fontSize: 13 }}>{e.oem_number || "—"}</td>
                                            <td style={{ color: "var(--bm-good)", fontVariantNumeric: "tabular-nums", fontSize: 13 }}>{e.aftermarket_number || "—"}</td>
                                            <td style={{ color: "var(--bm-text-muted)", fontSize: 13 }}>{e.vendor || "—"}</td>
                                            <td style={{ fontVariantNumeric: "tabular-nums", fontSize: 13 }}>{formatMoney(e.amount, e.currency)}</td>
                                            <td style={{ fontVariantNumeric: "tabular-nums", fontSize: 13 }}>{e.quantity ?? "—"}</td>
                                            <td style={{ color: "var(--bm-text-muted)", fontSize: 12 }}>{e.notes || ""}</td>
                                            <td style={{ textAlign: "right" }}>
                                                <RowActions entry={e} canWrite={canWrite} onEdit={onEdit} onDelete={onDelete} />
                                            </td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            );
                        })}

                        {/* Flat mode (legacy) */}
                        {!grouped && filtered.map((e) => (
                            <tr key={e.id} data-testid={`entry-row-${e.id}`} onClick={canWrite ? () => onEdit(e) : undefined} style={{ cursor: canWrite ? "pointer" : "default" }}>
                                <td></td>
                                <td style={{ width: 56 }}>
                                    {e.photo_data ? (
                                        isPdfDataUri(e.photo_data) ? (
                                            <div style={{ width: 40, height: 40, borderRadius: 6, background: "rgba(91,185,232,0.1)", border: "1px solid var(--bm-border-soft)", display: "grid", placeItems: "center", color: "var(--bm-blue)" }}>
                                                <FileText size={18} />
                                            </div>
                                        ) : (
                                            <img src={e.photo_data} alt="" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6, border: "1px solid var(--bm-border-soft)" }} />
                                        )
                                    ) : (
                                        <div style={{ width: 40, height: 40, borderRadius: 6, background: "rgba(91,185,232,0.05)", border: "1px dashed var(--bm-border-soft)", display: "grid", placeItems: "center", color: "var(--bm-text-dim)" }}>
                                            <Camera size={14} />
                                        </div>
                                    )}
                                </td>
                                <td style={{ fontWeight: 600 }}>
                                    {e.title}
                                    {e.added_by_user_id && currentUserId && e.added_by_user_id !== currentUserId && e.added_by_name && (
                                        <div data-testid={`entry-attrib-${e.id}`} style={{ fontSize: 11, color: "var(--bm-text-muted)", fontWeight: 400, marginTop: 3, letterSpacing: "0.02em" }}>
                                            added by {e.added_by_name}
                                        </div>
                                    )}
                                </td>
                                <td style={{ color: "var(--bm-blue)", fontVariantNumeric: "tabular-nums" }}>{e.oem_number || "—"}</td>
                                <td style={{ color: "var(--bm-good)", fontVariantNumeric: "tabular-nums" }}>{e.aftermarket_number || "—"}</td>
                                <td style={{ color: "var(--bm-text-muted)" }}>{e.vendor || "—"}</td>
                                <td style={{ fontVariantNumeric: "tabular-nums" }}>{formatMoney(e.amount, e.currency)}</td>
                                <td style={{ fontVariantNumeric: "tabular-nums" }}>{e.quantity ?? "—"}</td>
                                <td style={{ color: "var(--bm-text-muted)", fontSize: 13 }}>{e.notes || ""}</td>
                                <td style={{ textAlign: "right" }}>
                                    <RowActions entry={e} canWrite={canWrite} onEdit={onEdit} onDelete={onDelete} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            </div>
        </div>
    );
}

// Human label used in the delete-confirmation copy, keyed by entry category.
const DELETE_LABELS = {
    "Fuel": "fuel log",
    "Maintenance": "maintenance record",
    "Parts": "part",
    "Cruising Log": "cruising log",
};

function DeleteConfirmModal({ entry, busy, onCancel, onConfirm }) {
    const label = DELETE_LABELS[entry.category] || "entry";
    // Portaled to <body>: ancestor transforms/animations (e.g. .bm-card hover)
    // otherwise re-anchor position:fixed and shove the dialog off-screen.
    return createPortal(
        <div
            data-testid="delete-confirm-modal"
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "grid", placeItems: "center", padding: 20, zIndex: 130 }}
            onClick={onCancel}
        >
            <div className="bm-card" style={{ padding: 26, maxWidth: 420, width: "100%" }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <AlertTriangle size={20} color="#f87171" />
                    <h3 className="bm-serif" style={{ margin: 0, fontSize: 20, fontWeight: 500 }}>Delete {label}?</h3>
                </div>
                <p style={{ color: "var(--bm-text-muted)", fontSize: 14, lineHeight: 1.6, margin: "0 0 6px" }}>
                    Are you sure you want to delete this {label}? This action cannot be undone.
                </p>
                {entry.title && (
                    <p style={{ color: "var(--bm-text)", fontSize: 13, margin: "0 0 6px", fontWeight: 500 }}>
                        "{entry.title}"{entry.date ? ` · ${(entry.date || "").slice(0, 10)}` : ""}
                    </p>
                )}
                {(entry.category === "Fuel" || entry.category === "Cruising Log") && (
                    <p style={{ color: "var(--bm-text-dim)", fontSize: 12, margin: "0 0 6px" }}>
                        Fuel statistics (MPG / GPH) will be recalculated from the remaining entries.
                    </p>
                )}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
                    <button data-testid="delete-confirm-cancel-btn" className="bm-btn bm-btn-ghost" onClick={onCancel} disabled={busy} style={{ padding: "8px 16px" }}>
                        Cancel
                    </button>
                    <button
                        data-testid="delete-confirm-btn"
                        onClick={onConfirm}
                        disabled={busy}
                        style={{
                            padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(248,113,113,0.6)",
                            background: "rgba(248,113,113,0.15)", color: "#f87171", fontWeight: 600,
                            fontSize: 14, cursor: busy ? "wait" : "pointer",
                        }}
                    >
                        {busy ? "Deleting…" : "Delete"}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

// Three-dot "⋯" action menu shown on every log row — Edit + Delete.
// Menu renders with position:fixed (computed from the button rect) so it
// never gets clipped by the table's overflow-x scroll container.
function RowActions({ entry, canWrite = true, onEdit, onDelete }) {
    const [menuPos, setMenuPos] = useState(null);
    if (!canWrite) return null;
    const toggle = (ev) => {
        ev.stopPropagation();
        if (menuPos) { setMenuPos(null); return; }
        const r = ev.currentTarget.getBoundingClientRect();
        setMenuPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
    };
    const itemStyle = {
        display: "flex", alignItems: "center", gap: 8, width: "100%",
        padding: "9px 14px", background: "transparent", border: "none",
        color: "var(--bm-text)", fontSize: 13, cursor: "pointer", textAlign: "left",
    };
    return (
        <span onClick={(ev) => ev.stopPropagation()}>
            <button
                data-testid={`entry-actions-${entry.id}`}
                className="bm-btn bm-btn-ghost"
                onClick={toggle}
                title="Actions"
                style={{ padding: "4px 9px" }}
            >
                <MoreHorizontal size={15} />
            </button>
            {menuPos && createPortal(
                <>
                    <div style={{ position: "fixed", inset: 0, zIndex: 119 }} onClick={() => setMenuPos(null)} />
                    <div style={{
                        position: "fixed", top: menuPos.top, right: menuPos.right, zIndex: 120,
                        background: "var(--bm-bg-1)", border: "1px solid var(--bm-border-soft)",
                        borderRadius: 8, boxShadow: "0 10px 30px rgba(0,0,0,0.5)", minWidth: 132, overflow: "hidden",
                    }}>
                        <button data-testid={`entry-edit-${entry.id}`} style={itemStyle}
                            onClick={() => { setMenuPos(null); onEdit(entry); }}>
                            <Pencil size={13} /> Edit
                        </button>
                        <button data-testid={`entry-delete-${entry.id}`} style={{ ...itemStyle, color: "#f87171", borderTop: "1px solid var(--bm-border-soft)" }}
                            onClick={() => { setMenuPos(null); onDelete(entry); }}>
                            <Trash2 size={13} /> Delete
                        </button>
                    </div>
                </>,
                document.body
            )}
        </span>
    );
}

function CategoryView({ category, entries, fields, canWrite = true, customFields = [], engineHours = 0, currentUserId, onAdd, onEdit, onDelete }) {
    return (
        <div data-testid={`category-view-${slug(category)}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <div>
                    <h2 className="bm-serif" style={{ fontSize: 32, margin: 0, fontWeight: 500 }}>{category}</h2>
                    <p style={{ color: "var(--bm-text-muted)", margin: "4px 0 0", fontSize: 14 }}>{entries.length} entries</p>
                </div>
                <button data-testid="add-entry-btn" className="bm-btn bm-btn-gold" onClick={onAdd} disabled={!canWrite} style={{ opacity: canWrite ? 1 : 0.4, cursor: canWrite ? "pointer" : "not-allowed" }}><Plus size={15} /> Add entry</button>
            </div>
            <div className="bm-card" style={{ overflow: "hidden" }}>
                <div style={{ overflowX: "auto" }}>
                <table className="bm-table">
                    <thead>
                        <tr>
                            {fields.showPhoto && <th></th>}
                            <th>Date</th>
                            <th>Title</th>
                            {fields.showVendor && <th>Vendor</th>}
                            {fields.showQuantity && <th>{fields.qtyLabel}</th>}
                            {fields.showAmount && <th>{fields.amountLabel}</th>}
                            {fields.showHours && <th>Hours</th>}
                            {customFields.map((cf) => <th key={cf.key} data-testid={`cf-col-${cf.key}`}>{cf.label}</th>)}
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.length === 0 && (
                            <tr><td colSpan={8 + customFields.length} style={{ textAlign: "center", padding: 36, color: "var(--bm-text-muted)" }}>Nothing logged yet. Tap "Add entry" to start.</td></tr>
                        )}
                        {entries.map((e) => (
                            <tr key={e.id} data-testid={`entry-row-${e.id}`} onClick={canWrite ? () => onEdit(e) : undefined} style={{ cursor: canWrite ? "pointer" : "default" }}>
                                {fields.showPhoto && (
                                    <td style={{ width: 56 }}>
                                        {e.photo_data ? (
                                            isPdfDataUri(e.photo_data) ? (
                                                <div data-testid={`entry-photo-${e.id}`} style={{ width: 40, height: 40, borderRadius: 6, background: "rgba(91,185,232,0.1)", border: "1px solid var(--bm-border-soft)", display: "grid", placeItems: "center", color: "var(--bm-blue)" }}>
                                                    <FileText size={18} />
                                                </div>
                                            ) : (
                                                <img
                                                    data-testid={`entry-photo-${e.id}`}
                                                    src={e.photo_data}
                                                    alt=""
                                                    style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6, border: "1px solid var(--bm-border-soft)" }}
                                                />
                                            )
                                        ) : (
                                            <div style={{ width: 40, height: 40, borderRadius: 6, background: "rgba(91,185,232,0.05)", border: "1px dashed var(--bm-border-soft)", display: "grid", placeItems: "center", color: "var(--bm-text-dim)" }}>
                                                <Camera size={14} />
                                            </div>
                                        )}
                                    </td>
                                )}
                                <td style={{ color: "var(--bm-text-muted)", fontVariantNumeric: "tabular-nums" }}>{(e.date || "").slice(0, 10)}</td>
                                <td>
                                    {e.title}
                                    {(() => {
                                        const s = computeMaintenanceStatus(e, engineHours);
                                        if (s === "done") return <span className="bm-tag bm-tag-good" style={{ marginLeft: 8 }}>done</span>;
                                        if (s === "overdue") return <span className="bm-tag" style={{ marginLeft: 8, color: "#fca5a5", borderColor: "rgba(248,113,113,0.5)", background: "rgba(248,113,113,0.12)" }}>overdue</span>;
                                        if (s === "upcoming") return <span className="bm-tag" style={{ marginLeft: 8, color: "#fde68a", borderColor: "rgba(251,191,36,0.5)", background: "rgba(251,191,36,0.12)" }}>upcoming</span>;
                                        if (s === "scheduled") return <span className="bm-tag bm-tag-warn" style={{ marginLeft: 8 }}>scheduled</span>;
                                        return null;
                                    })()}
                                    {e.added_by_user_id && currentUserId && e.added_by_user_id !== currentUserId && e.added_by_name && (
                                        <div data-testid={`entry-attrib-${e.id}`} style={{ fontSize: 11, color: "var(--bm-text-muted)", marginTop: 3, letterSpacing: "0.02em" }}>
                                            added by {e.added_by_name}
                                        </div>
                                    )}
                                </td>
                                {fields.showVendor && <td style={{ color: "var(--bm-text-muted)" }}>{e.vendor || "—"}</td>}
                                {fields.showQuantity && <td style={{ fontVariantNumeric: "tabular-nums" }}>{e.quantity ?? "—"}</td>}
                                {fields.showAmount && <td style={{ fontVariantNumeric: "tabular-nums" }}>{formatMoney(e.amount, e.currency)}</td>}
                                {fields.showHours && <td style={{ fontVariantNumeric: "tabular-nums" }}>{e.hours_at_event ?? "—"}</td>}
                                {customFields.map((cf) => {
                                    const v = (e.custom_data || {})[cf.key];
                                    return <td key={cf.key} data-testid={`cf-cell-${e.id}-${cf.key}`} style={{ color: v == null || v === "" ? "var(--bm-text-dim)" : "var(--bm-text)", fontSize: 13 }}>{v == null || v === "" ? "—" : String(v)}</td>;
                                })}
                                <td style={{ textAlign: "right" }}>
                                    <RowActions entry={e} canWrite={canWrite} onEdit={onEdit} onDelete={onDelete} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            </div>
        </div>
    );
}


function CruisingLogView({ category, entries, trips = [], vesselId, canWrite = true, currentUserId, engines = [], onAdd, onEdit, onDelete, onTripsChanged, onAddEntryForTrip }) {
    const [newTripName, setNewTripName] = useState("");
    const [showNewTrip, setShowNewTrip] = useState(false);
    const [creating, setCreating] = useState(false);
    const [expandedTripId, setExpandedTripId] = useState(null);

    // Sort entries newest first by departure date
    const sortedEntries = useMemo(() => {
        return [...entries].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    }, [entries]);

    // Bucket entries by trip_id, and a "standalone" bucket for entries with no trip_id
    const entriesByTrip = useMemo(() => {
        const map = new Map();
        const standalone = [];
        for (const e of sortedEntries) {
            if (e.trip_id) {
                if (!map.has(e.trip_id)) map.set(e.trip_id, []);
                map.get(e.trip_id).push(e);
            } else {
                standalone.push(e);
            }
        }
        return { byTrip: map, standalone };
    }, [sortedEntries]);

    const sortedTrips = useMemo(() => {
        // Open trips first (most recent first), then closed
        return [...trips].sort((a, b) => {
            if (a.status !== b.status) return a.status === "open" ? -1 : 1;
            return (b.start_date || b.created_at || "").localeCompare(a.start_date || a.created_at || "");
        });
    }, [trips]);

    const createTrip = async () => {
        const name = newTripName.trim();
        if (!name) return;
        setCreating(true);
        try {
            const { data } = await api.post(`/vessels/${vesselId}/trips`, {
                name,
                start_date: new Date().toISOString().slice(0, 10),
            });
            setNewTripName("");
            setShowNewTrip(false);
            onTripsChanged && onTripsChanged();
            // Auto-expand the freshly created trip
            setTimeout(() => setExpandedTripId(data.id), 50);
        } catch (e) {
            alert(formatApiErrorDetail(e.response?.data?.detail) || "Could not create trip");
        } finally {
            setCreating(false);
        }
    };

    const toggleTripStatus = async (trip) => {
        const nextStatus = trip.status === "open" ? "closed" : "open";
        const patch = { status: nextStatus };
        if (nextStatus === "closed") patch.end_date = new Date().toISOString().slice(0, 10);
        try {
            await api.patch(`/vessels/${vesselId}/trips/${trip.id}`, patch);
            onTripsChanged && onTripsChanged();
        } catch (e) {
            alert(formatApiErrorDetail(e.response?.data?.detail) || "Could not update trip");
        }
    };

    const deleteTrip = async (trip) => {
        if (!window.confirm(`Delete trip "${trip.name}"? Daily logs will become standalone (not deleted).`)) return;
        try {
            await api.delete(`/vessels/${vesselId}/trips/${trip.id}`);
            onTripsChanged && onTripsChanged();
        } catch (e) {
            alert(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete trip");
        }
    };

    return (
        <div data-testid="cruising-log-view">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 14, marginBottom: 20 }}>
                <div>
                    <h2 className="bm-serif" style={{ fontSize: 32, margin: 0, fontWeight: 500 }}>Cruising Log</h2>
                    <p style={{ color: "var(--bm-text-muted)", margin: "4px 0 0", fontSize: 14 }}>
                        Log each day's run. Group days into trips for rolled-up totals.
                    </p>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                        data-testid="new-trip-btn"
                        className="bm-btn bm-btn-ghost"
                        onClick={() => setShowNewTrip(true)}
                        disabled={!canWrite}
                        style={{ opacity: canWrite ? 1 : 0.4, cursor: canWrite ? "pointer" : "not-allowed" }}
                    >
                        <Plus size={15} /> New trip
                    </button>
                    <button
                        data-testid="add-entry-btn"
                        className="bm-btn bm-btn-gold"
                        onClick={onAdd}
                        disabled={!canWrite}
                        style={{ opacity: canWrite ? 1 : 0.4, cursor: canWrite ? "pointer" : "not-allowed" }}
                    >
                        <Plus size={15} /> Log a day
                    </button>
                </div>
            </div>

            {showNewTrip && (
                <div data-testid="new-trip-form" className="bm-card" style={{ padding: 16, marginBottom: 18, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <input
                        data-testid="new-trip-name-input"
                        className="bm-input"
                        placeholder="Trip name (e.g. North to Sitka)"
                        value={newTripName}
                        onChange={(e) => setNewTripName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") createTrip(); }}
                        style={{ flex: 1, minWidth: 220 }}
                        autoFocus
                    />
                    <button data-testid="new-trip-save-btn" className="bm-btn bm-btn-gold" onClick={createTrip} disabled={!newTripName.trim() || creating} style={{ padding: "8px 14px" }}>
                        {creating ? "Creating…" : "Create"}
                    </button>
                    <button className="bm-btn bm-btn-ghost" onClick={() => { setShowNewTrip(false); setNewTripName(""); }} style={{ padding: "8px 14px" }}>
                        Cancel
                    </button>
                </div>
            )}

            {/* Trip cards (with their daily logs) */}
            {sortedTrips.length > 0 && (
                <div data-testid="trip-list" style={{ display: "grid", gap: 12, marginBottom: 24 }}>
                    {sortedTrips.map((t) => {
                        const days = entriesByTrip.byTrip.get(t.id) || [];
                        const isExpanded = expandedTripId === t.id;
                        return (
                            <article key={t.id} data-testid={`trip-card-${t.id}`} className="bm-card" style={{ padding: 0, overflow: "hidden" }}>
                                <div
                                    onClick={() => setExpandedTripId(isExpanded ? null : t.id)}
                                    style={{ padding: "14px 18px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", borderBottom: isExpanded ? "1px solid var(--bm-border-soft)" : "none" }}
                                >
                                    <div style={{ flex: 1, minWidth: 220 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: "var(--bm-text)" }}>{t.name}</h3>
                                            <span className={t.status === "open" ? "bm-tag bm-tag-good" : "bm-tag"} style={{ fontSize: 10 }}>
                                                {t.status}
                                            </span>
                                        </div>
                                        <div style={{ fontSize: 12, color: "var(--bm-text-muted)", marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
                                            {t.day_count} {t.day_count === 1 ? "day" : "days"}
                                            {t.total_hours_underway > 0 && ` · ${t.total_hours_underway} hrs underway`}
                                            {t.total_distance_nm > 0 && ` · ${t.total_distance_nm} nm`}
                                            {t.avg_speed_kn != null && <span style={{ color: "var(--bm-gold)" }}> · {t.avg_speed_kn} kn avg</span>}
                                        </div>
                                        {(t.start_date || t.end_date) && (
                                            <div style={{ fontSize: 11, color: "var(--bm-text-dim)", marginTop: 2 }}>
                                                {t.start_date || "—"}{t.end_date ? ` → ${t.end_date}` : ""}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ fontSize: 12, color: "var(--bm-text-dim)" }}>
                                        {isExpanded ? "▾" : "▸"} {days.length} {days.length === 1 ? "log" : "logs"}
                                    </div>
                                </div>
                                {isExpanded && (
                                    <div style={{ padding: "12px 18px", background: "var(--bm-bg-1)" }}>
                                        {days.length === 0 && (
                                            <div style={{ color: "var(--bm-text-muted)", fontSize: 13, padding: "6px 0 12px" }}>
                                                No daily logs yet. Tap "Log a day" to add the first one.
                                            </div>
                                        )}
                                        {days.length > 0 && (
                                            <div style={{ display: "grid", gap: 8, marginBottom: 10 }}>
                                                {days.map((e) => (
                                                    <DailyLogRow key={e.id} entry={e} engines={engines} canWrite={canWrite} currentUserId={currentUserId} onEdit={onEdit} onDelete={onDelete} />
                                                ))}
                                            </div>
                                        )}
                                        {canWrite && (
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, gap: 8, flexWrap: "wrap" }}>
                                                <button
                                                    data-testid={`trip-add-day-btn-${t.id}`}
                                                    className="bm-btn bm-btn-gold"
                                                    onClick={() => onAddEntryForTrip && onAddEntryForTrip(t.id)}
                                                    style={{ padding: "6px 12px", fontSize: 13 }}
                                                >
                                                    <Plus size={13} /> Log a day
                                                </button>
                                                <div style={{ display: "flex", gap: 6 }}>
                                                    <button
                                                        data-testid={`trip-toggle-btn-${t.id}`}
                                                        className="bm-btn bm-btn-ghost"
                                                        onClick={() => toggleTripStatus(t)}
                                                        style={{ padding: "6px 10px", fontSize: 12 }}
                                                    >
                                                        {t.status === "open" ? "Close trip" : "Re-open"}
                                                    </button>
                                                    <button
                                                        data-testid={`trip-delete-btn-${t.id}`}
                                                        className="bm-btn bm-btn-ghost"
                                                        onClick={() => deleteTrip(t)}
                                                        style={{ padding: "6px 10px", fontSize: 12, color: "#fca5a5" }}
                                                    >
                                                        Delete trip
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </article>
                        );
                    })}
                </div>
            )}

            {/* Standalone entries (not associated with any trip) */}
            {entriesByTrip.standalone.length > 0 && (
                <div data-testid="standalone-section">
                    <h3 style={{ fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--bm-text-muted)", margin: "0 0 12px" }}>
                        Standalone logs
                    </h3>
                    <div style={{ display: "grid", gap: 8 }}>
                        {entriesByTrip.standalone.map((e) => (
                            <DailyLogRow key={e.id} entry={e} engines={engines} canWrite={canWrite} currentUserId={currentUserId} onEdit={onEdit} onDelete={onDelete} />
                        ))}
                    </div>
                </div>
            )}

            {sortedTrips.length === 0 && entriesByTrip.standalone.length === 0 && (
                <div className="bm-card" style={{ padding: 36, textAlign: "center", color: "var(--bm-text-muted)" }}>
                    No trips or logs yet. Tap "New trip" to start a multi-day journey, or "Log a day" for a standalone outing.
                </div>
            )}
        </div>
    );
}

function DailyLogRow({ entry: e, engines, canWrite, currentUserId, onEdit, onDelete }) {
    const engine = engines.find((eng) => eng.id === e.engine_id);
    const hu = e.hours_underway != null
        ? e.hours_underway
        : (e.start_hours != null && e.end_hours != null ? Number((e.end_hours - e.start_hours).toFixed(1)) : null);
    const showAttrib = e.added_by_user_id && currentUserId && e.added_by_user_id !== currentUserId && e.added_by_name;
    return (
        <article
            data-testid={`entry-row-${e.id}`}
            className="bm-card"
            onClick={canWrite ? () => onEdit(e) : undefined}
            style={{ padding: "12px 14px", cursor: canWrite ? "pointer" : "default", border: "1px solid var(--bm-border-soft)" }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontSize: 13, color: "var(--bm-text)", fontWeight: 500 }}>
                        {e.title || "Daily log"}{" "}
                        <span style={{ fontSize: 11, color: "var(--bm-blue)", marginLeft: 6 }}>{(e.date || "").slice(0, 10)}</span>
                        {engine && <span className="bm-tag" style={{ marginLeft: 8, fontSize: 10 }}>{engine.label}</span>}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--bm-text-muted)", marginTop: 3, fontVariantNumeric: "tabular-nums", display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {e.start_hours != null && e.end_hours != null && (
                            <span>{e.start_hours} → {e.end_hours} hrs</span>
                        )}
                        {hu != null && <span>· {hu} hrs underway</span>}
                        {e.distance_nm != null && <span>· {e.distance_nm} nm</span>}
                        {e.distance_nm != null && hu != null && hu > 0 && (
                            <span style={{ color: "var(--bm-gold)" }}>· {(e.distance_nm / hu).toFixed(1)} kn avg</span>
                        )}
                    </div>
                    {showAttrib && (
                        <div data-testid={`entry-attrib-${e.id}`} style={{ fontSize: 10.5, color: "var(--bm-text-dim)", marginTop: 3 }}>
                            added by {e.added_by_name}
                        </div>
                    )}
                </div>
                {canWrite && (
                    <RowActions entry={e} canWrite={canWrite} onEdit={onEdit} onDelete={onDelete} />
                )}
            </div>
        </article>
    );
}


function ActivityView({ entries, engines = [], currentUserId }) {
    // Sort newest first by created_at (fallback to date), group by day.
    const grouped = useMemo(() => {
        const sorted = [...entries].sort((a, b) => {
            const at = a.created_at || a.date || "";
            const bt = b.created_at || b.date || "";
            return bt.localeCompare(at);
        });
        const byDay = new Map();
        for (const e of sorted) {
            const stamp = e.created_at || e.date || "";
            const day = stamp.slice(0, 10) || "unknown";
            if (!byDay.has(day)) byDay.set(day, []);
            byDay.get(day).push(e);
        }
        return Array.from(byDay.entries());
    }, [entries]);

    const fmtDay = (iso) => {
        if (!iso || iso === "unknown") return "Undated";
        const d = new Date(iso + "T00:00:00");
        const today = new Date();
        const yest = new Date(); yest.setDate(today.getDate() - 1);
        const same = (a, b) => a.toDateString() === b.toDateString();
        if (same(d, today)) return "Today";
        if (same(d, yest)) return "Yesterday";
        return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: d.getFullYear() === today.getFullYear() ? undefined : "numeric" });
    };

    const fmtTime = (iso) => {
        if (!iso) return "";
        try {
            return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
        } catch { return ""; }
    };

    if (entries.length === 0) {
        return (
            <div data-testid="activity-view">
                <h2 className="bm-serif" style={{ fontSize: 32, margin: "0 0 6px", fontWeight: 500 }}>Activity</h2>
                <p style={{ color: "var(--bm-text-muted)", margin: "0 0 24px", fontSize: 14 }}>Chronological log of every entry on this vessel.</p>
                <div className="bm-card" style={{ padding: 36, textAlign: "center", color: "var(--bm-text-muted)" }}>
                    Nothing logged yet. Add your first entry from any category tab.
                </div>
            </div>
        );
    }

    return (
        <div data-testid="activity-view">
            <h2 className="bm-serif" style={{ fontSize: 32, margin: "0 0 6px", fontWeight: 500 }}>Activity</h2>
            <p style={{ color: "var(--bm-text-muted)", margin: "0 0 24px", fontSize: 14 }}>
                {entries.length} {entries.length === 1 ? "entry" : "entries"} across all categories, newest first.
            </p>

            <div className="bm-card" style={{ padding: "8px 0" }}>
                {grouped.map(([day, items]) => (
                    <div key={day} data-testid={`activity-day-${day}`}>
                        <div style={{
                            display: "flex", alignItems: "center", gap: 12,
                            padding: "14px 22px 6px",
                            fontSize: 11, letterSpacing: "0.18em",
                            color: "var(--bm-gold)", textTransform: "uppercase",
                        }}>
                            <span>{fmtDay(day)}</span>
                            <span style={{ flex: 1, height: 1, background: "var(--bm-border-soft)" }} />
                            <span style={{ color: "var(--bm-text-dim)" }}>{items.length}</span>
                        </div>
                        {items.map((e) => {
                            const isMine = e.added_by_user_id && currentUserId && e.added_by_user_id === currentUserId;
                            const who = e.added_by_name
                                ? (isMine ? "You" : e.added_by_name)
                                : null;
                            const engine = e.engine_id ? engines.find((eng) => eng.id === e.engine_id) : null;
                            return (
                                <div
                                    key={e.id}
                                    data-testid={`activity-row-${e.id}`}
                                    style={{
                                        display: "flex", alignItems: "flex-start", gap: 14,
                                        padding: "12px 22px",
                                        borderBottom: "1px solid rgba(28,63,99,0.25)",
                                    }}
                                >
                                    <div style={{
                                        width: 8, height: 8, borderRadius: "50%",
                                        background: "var(--bm-gold)", opacity: 0.6,
                                        marginTop: 8, flexShrink: 0,
                                    }} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 14, color: "var(--bm-text)", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            <span style={{ color: "var(--bm-text-muted)" }}>{who ? `${who} logged ` : "Logged "}</span>
                                            <strong style={{ fontWeight: 600 }}>{e.title}</strong>
                                            <span style={{ color: "var(--bm-text-muted)" }}> in </span>
                                            <span style={{ color: "var(--bm-blue)" }}>{e.category}</span>
                                            {engine && (
                                                <span style={{ color: "var(--bm-text-muted)" }}> · <span style={{ color: "var(--bm-gold)" }}>{engine.label}</span></span>
                                            )}
                                        </div>
                                        <div style={{ fontSize: 12, color: "var(--bm-text-dim)", marginTop: 3, display: "flex", gap: 12, flexWrap: "wrap" }}>
                                            {e.amount != null && e.amount !== "" && (
                                                <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatMoney(e.amount, e.currency)}</span>
                                            )}
                                            {e.quantity != null && e.quantity !== "" && (
                                                <span style={{ fontVariantNumeric: "tabular-nums" }}>{e.quantity} {e.unit || ""}</span>
                                            )}
                                            {e.hours_at_event != null && (
                                                <span style={{ fontVariantNumeric: "tabular-nums" }}>{e.hours_at_event} hrs</span>
                                            )}
                                            {e.hours_underway != null && (
                                                <span style={{ fontVariantNumeric: "tabular-nums" }}>{e.hours_underway} hrs underway</span>
                                            )}
                                            {e.distance_nm != null && (
                                                <span style={{ fontVariantNumeric: "tabular-nums" }}>{e.distance_nm} nm</span>
                                            )}
                                            {e.date_to && (
                                                <span>→ {e.date_to.slice(0, 10)}</span>
                                            )}
                                            {e.vendor && <span>· {e.vendor}</span>}
                                            <span style={{ marginLeft: "auto", color: "var(--bm-text-dim)" }}>{fmtTime(e.created_at)}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
}


function Panel({ title, children }) {
    return (
        <div className="bm-card" style={{ padding: 22 }}>
            <h3 className="bm-serif" style={{ fontSize: 20, margin: "0 0 14px", fontWeight: 500 }}>{title}</h3>
            <div>{children}</div>
        </div>
    );
}
function Row({ title, sub }) {
    return (
        <div style={{ padding: "10px 0", borderBottom: "1px solid rgba(28,63,99,0.4)" }}>
            <div style={{ fontSize: 14 }}>{title}</div>
            {sub && <div style={{ fontSize: 12, color: "var(--bm-text-muted)", marginTop: 2 }}>{sub}</div>}
        </div>
    );
}
function Empty({ children }) {
    return <div style={{ color: "var(--bm-text-muted)", fontSize: 13, padding: "10px 0" }}>{children}</div>;
}

/**
 * Hoisted-to-module-scope so React doesn't recreate the component on every
 * keystroke (which would unmount/remount the input and drop the keyboard
 * after one character — the bug we hit on the Electronics form).
 */
function VesselInfoRow({ children, cols = 2 }) {
    return (
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12, marginBottom: 12 }}>
            {children}
        </div>
    );
}

function VesselInfoField({ label, value, onChange, testid, placeholder, type = "text", area = false }) {
    return (
        <div>
            <label className="bm-label">{label}</label>
            {area ? (
                <textarea
                    data-testid={testid}
                    className="bm-input"
                    rows={2}
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    data-gramm="false"
                    data-gramm_editor="false"
                    data-enable-grammarly="false"
                    spellCheck={false}
                />
            ) : (
                <input
                    data-testid={testid}
                    className="bm-input"
                    type={type}
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    data-gramm="false"
                    data-gramm_editor="false"
                    data-enable-grammarly="false"
                    spellCheck={false}
                />
            )}
        </div>
    );
}

function daysLeft(iso) {
    if (!iso) return 0;
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}
function slug(s) { return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); }


// Sticky compliance banner — surfaces expired/expiring flares + fire
// extinguishers AND (for Pro users) hull-insurance renewals at the very top
// of the Dashboard. Hidden when there's nothing to warn about, or when the
// user is already on the Safety or Insurance tab (redundant).
function SafetyAlertBanner({ items, vessel, onOpenSafety, onOpenInsurance, active, isPro }) {
    if (active) return null;
    const overdue = (items || []).filter((it) => it.status === "red");
    const expiring = (items || []).filter((it) => it.status === "amber");

    // Insurance status — only surfaces for Pro users (the whole feature is
    // Pro-gated). Free users still see safety alerts above.
    const insuranceStatus = isPro ? computeInsuranceStatus(vessel?.hull_insurance_renewal) : { level: "none" };
    const insuranceRed = insuranceStatus.level === "red";
    const insuranceAmber = insuranceStatus.level === "amber";

    if (overdue.length === 0 && expiring.length === 0 && !insuranceRed && !insuranceAmber) return null;

    const totalRed = overdue.length + (insuranceRed ? 1 : 0);
    const totalAmber = expiring.length + (insuranceAmber ? 1 : 0);
    const isRed = totalRed > 0;
    const bg = isRed ? "rgba(255, 89, 89, 0.10)" : "rgba(251, 191, 36, 0.10)";
    const border = isRed ? "rgba(255, 89, 89, 0.5)" : "rgba(251, 191, 36, 0.55)";
    const color = isRed ? "var(--bm-bad)" : "var(--bm-warn)";

    // headline pieces
    const parts = [];
    if (totalRed > 0) parts.push(`${totalRed} overdue`);
    if (totalAmber > 0) parts.push(`${totalAmber} expiring soon`);
    const headline = `Compliance: ${parts.join(" · ")}`;

    // Build a compact list of at-risk item names for the subline
    const detailChunks = [];
    if (insuranceRed) detailChunks.push(`Hull insurance lapsed ${Math.abs(insuranceStatus.days)}d ago`);
    else if (insuranceAmber) detailChunks.push(`Hull insurance renews in ${insuranceStatus.days}d`);
    if (overdue.length > 0) {
        detailChunks.push(overdue.slice(0, 2).map((it) => it.label).join(", ") + (overdue.length > 2 ? ` +${overdue.length - 2} more` : ""));
    }
    if (expiring.length > 0) {
        detailChunks.push(expiring.slice(0, 2).map((it) => it.label).join(", ") + (expiring.length > 2 ? ` +${expiring.length - 2} more` : ""));
    }

    // If insurance is the primary issue (red trumps safety amber), the CTA
    // jumps to Insurance. Otherwise the safety tab is more useful.
    const insuranceIsPrimary = insuranceRed || (insuranceAmber && overdue.length === 0);
    const onReview = insuranceIsPrimary ? onOpenInsurance : onOpenSafety;
    const ctaLabel = insuranceIsPrimary ? "Review insurance →" : "Review safety →";

    return (
        <div
            data-testid="safety-alert-banner"
            data-status={isRed ? "red" : "amber"}
            role="alert"
            style={{
                padding: "14px 16px",
                marginBottom: 18,
                borderRadius: 10,
                background: bg,
                border: `1px solid ${border}`,
                display: "flex",
                alignItems: "center",
                gap: 14,
                flexWrap: "wrap",
                color,
            }}
        >
            <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontSize: 14, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 8 }}>
                    {isRed ? <AlertCircle size={16} /> : <AlertTriangle size={16} />}
                    {headline}
                </div>
                <div style={{ fontSize: 12, color: "var(--bm-text-muted)", marginTop: 4 }}>
                    {detailChunks.join(" · ")}
                </div>
            </div>
            <button
                data-testid="safety-alert-open-btn"
                onClick={onReview}
                className="bm-btn bm-btn-ghost"
                style={{ padding: "6px 14px", fontSize: 13, borderColor: border, color }}
            >
                {ctaLabel}
            </button>
        </div>
    );
}
